/* DTC core v2 - booking build */
/* ============================================================
   DTC core behaviours - dtc-core.js
   Progressive enhancement only: every dtc- section works with no
   JS at all (native details accordions, scrollable card rails).
   This file adds slider arrows, single-open FAQ behaviour and the
   scroll-reveal polish.
   ============================================================ */
(function () {
  'use strict';
  if (window.__dtcCoreLoaded) { return; }
  window.__dtcCoreLoaded = true;

  /* ONE SCROLL LOCK FOR EVERY MODAL (audit pdp-05, MOT-12). Four product-page
     dialogs locked the page four different ways, and three not at all. Every
     <dialog> opened with showModal() now adds the same class to <html>, and the
     lock lifts when the last modal closes. The class keeps the scrollbar's
     gutter, so the page does not shift sideways on Windows. */
  (function (proto) {
    if (!proto || !proto.showModal || proto.__tdhqLock) { return; }
    var show = proto.showModal;
    proto.__tdhqLock = true;
    proto.showModal = function () {
      show.apply(this, arguments);
      document.documentElement.classList.add('tdhq-modal-open');
      this.addEventListener('close', function () {
        var still = false;
        try { still = !!document.querySelector('dialog[open]:modal'); } catch (err) { still = !!document.querySelector('dialog[open]'); }
        if (!still) { document.documentElement.classList.remove('tdhq-modal-open'); }
      }, { once: true });
    };
  })(window.HTMLDialogElement && window.HTMLDialogElement.prototype);

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduce = motionQuery.matches;
  function updateMotionPreference(e) { reduce = e.matches; }
  if (motionQuery.addEventListener) { motionQuery.addEventListener('change', updateMotionPreference); }
  else if (motionQuery.addListener) { motionQuery.addListener(updateMotionPreference); }

  /* SHARED BY EVERY THEME SCRIPT (21 Oct, wf17/motion1021).
     tdhqMotion.behavior(): 'auto' when the visitor asks for reduced motion (read at call time, so a change of the
       system setting applies at once), otherwise 'smooth'. A script's explicit behavior:'smooth' overrides the CSS
       reduced-motion rule, so scripted scrolls ask this instead.
     tdhqImgReady(img, ok, fail): ok() once a preloaded picture can be shown, fail() only when it truly has no
       pixels. decode() alone was trusted both ways: a picture that failed to load replaced a working one, and
       Safari can reject decode() for a picture that loaded fine; the pixels decide. */
  window.tdhqMotion = window.tdhqMotion || {
    behavior: function () {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
  };
  window.tdhqImgReady = window.tdhqImgReady || function (img, ok, fail) {
    var done = false;
    var good = function () { if (!done) { done = true; ok(); } };
    var bad = function () { if (!done) { done = true; if (fail) { fail(); } } };
    var judge = function () {
      if (img.complete) { if (img.naturalWidth > 0) { good(); } else { bad(); } return; }
      img.addEventListener('load', good, { once: true });
      img.addEventListener('error', bad, { once: true });
    };
    if (img.decode) { img.decode().then(good, judge); } else { judge(); }
  };

  /* ---- slider arrows for card sections set to "Slider" ---- */
  /* rAF-coalesce a handler so N sliders on one page do not each force a synchronous
     layout read per resize tick. The final state is identical, one frame later. */
  function coalesced(fn) {
    var queued = false;
    return function () {
      if (queued) { return; }
      queued = true;
      requestAnimationFrame(function () { queued = false; fn(); });
    };
  }

  /* WIDTH CHANGES ONLY (owner 20 Oct 2026: phone scrolling stutters). A phone's
     address bar showing or hiding resizes the window's HEIGHT at every change of
     scroll direction; rails, carousels and logo fits depend on the width only,
     so they skip those resizes instead of measuring again mid-scroll. */
  var widthFns = [], lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) { return; }
    lastWidth = window.innerWidth;
    for (var i = 0; i < widthFns.length; i++) { widthFns[i](); }
  });
  function onWidth(fn) { widthFns.push(fn); }

  /* READ ALL, THEN WRITE ALL, ONCE A FRAME. Each rail used to measure itself
     and write straight away, one rail after another, so every measurement came
     right after the previous rail's write and the browser redid style and
     layout once per rail (Chrome trace of the live home page, 25 Sep 2026:
     2.6 s of forced reflow at 4x CPU, most of it rail start-up). A frameJob is
     a read (measure, no writes) and a write (no measuring); every job queued in
     a frame measures first, then every job writes, so the page lays out once.
     Calling the returned function queues the job; it runs at most once a frame. */
  var frameJobs = [];
  var frameRaf = 0;
  function runFrameJobs() {
    frameRaf = 0;
    var jobs = frameJobs;
    frameJobs = [];
    var seen = [];
    for (var i = 0; i < jobs.length; i++) {
      jobs[i].queued = false;
      try { seen.push({ ok: true, v: jobs[i].read() }); } catch (err) { seen.push({ ok: false }); }
    }
    for (var k = 0; k < jobs.length; k++) {
      if (!seen[k].ok) { continue; }
      try { jobs[k].write(seen[k].v); } catch (err) {}
    }
  }
  function frameJob(read, write) {
    var job = { read: read, write: write, queued: false };
    return function () {
      if (job.queued) { return; }
      job.queued = true;
      frameJobs.push(job);
      if (!frameRaf) { frameRaf = requestAnimationFrame(runFrameJobs); }
    };
  }

  function initCarousel(root) {
    if (!root || root.dataset.dtcInit) { return; }
    root.dataset.dtcInit = '1';
    var track = root.querySelector('[data-dtc-track]');
    var prev = root.querySelector('[data-dtc-prev]');
    var next = root.querySelector('[data-dtc-next]');
    if (!track) { return; }

    function step() {
      var card = track.firstElementChild;
      if (!card) { return 300; }
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 20;
      return card.getBoundingClientRect().width + gap;
    }

    /* ---- position bar under the rail ----
       Width is the share of the row that is on screen and the offset is how
       far through it we are, so the thumb reads as a scrollbar for the cards.
       It is translated rather than positioned with `left`, which keeps it on
       the compositor while the rail is being dragged. */
    /* Nine different sections render an arrow pair, and every one of them
       should get the position bar: it is what tells a visitor the row can be
       pulled. The bar is pure enhancement - it reports and drives a scroll
       position that already works without it - so it is built here rather than
       threaded through nine templates, and a section that ships its own
       (dtc-card-grid does) is left alone. */
    function ensureBar() {
      var existing = root.querySelector('[data-dtc-railbar]');
      if (existing) { return existing; }
      var nav = root.querySelector('.dtc-carousel__nav');
      if (!nav) { return null; }
      var el = document.createElement('div');
      el.className = 'dtc-railbar';
      el.setAttribute('data-dtc-railbar', '');
      var th = document.createElement('span');
      th.className = 'dtc-railbar__thumb';
      th.setAttribute('data-dtc-railthumb', '');
      el.appendChild(th);
      var foot = nav.parentElement;
      if (foot && foot.classList.contains('dtc-carousel__foot')) {
        /* that footer is already a 1fr | button | 1fr row with the arrows
           pinned right, so the bar takes the empty first column */
        foot.insertBefore(el, foot.firstChild);
      } else {
        var wrap = document.createElement('div');
        wrap.className = 'dtc-railfoot';
        nav.parentNode.insertBefore(wrap, nav);
        wrap.appendChild(el);
        wrap.appendChild(nav);
      }
      return el;
    }

    var bar = ensureBar();
    var thumb = bar ? bar.querySelector('[data-dtc-railthumb]') : null;

    /* m = one measurement of the rail (see frameJob): nothing here reads layout. */
    function paintBar(m) {
      if (!bar || !thumb) { return; }
      var ratio = m.cw / m.sw;
      if (!isFinite(ratio) || ratio >= 1) { return; }
      var w = Math.max(28, Math.round(m.barW * ratio));
      var travel = m.barW - w;
      var progress = m.max > 0 ? Math.min(1, Math.max(0, m.left / m.max)) : 0;
      thumb.style.width = w + 'px';
      thumb.style.transform = 'translateX(' + Math.round(travel * progress) + 'px)';
    }

    var update = frameJob(function () {
      var sw = track.scrollWidth, cw = track.clientWidth;
      return { sw: sw, cw: cw, max: sw - cw, left: track.scrollLeft, barW: bar ? bar.clientWidth : 0 };
    }, function (m) {
      /* Hide the arrows entirely when every card already fits (no overflow);
         re-shows them if the viewport shrinks enough to cause a scroll. */
      var wasStatic = root.classList.contains('dtc-carousel--static');
      var isStatic = m.max <= 4;
      root.classList.toggle('dtc-carousel--static', isStatic);
      if (prev) { prev.disabled = m.left <= 4; }
      if (next) { next.disabled = m.left >= m.max - 4; }
      /* the bar was measured while the rail was static: measure it again shown */
      if (wasStatic && !isStatic) { update(); return; }
      paintBar(m);
    });

    /* The bar is a control, not just a readout: press or drag anywhere along
       it and the rail jumps to that share of its travel. */
    if (bar) {
      var barDown = false;
      var seek = function (clientX) {
        var r = bar.getBoundingClientRect();
        var w = thumb ? thumb.getBoundingClientRect().width : 28;
        var travel = Math.max(1, r.width - w);
        var p = Math.min(1, Math.max(0, (clientX - r.left - w / 2) / travel));
        var max = track.scrollWidth - track.clientWidth;
        track.scrollLeft = max * p;
      };
      bar.addEventListener('pointerdown', function (e) {
        barDown = true;
        bar.classList.add('dtc-railbar--live');
        if (bar.setPointerCapture) { try { bar.setPointerCapture(e.pointerId); } catch (err) {} }
        /* the rail's own scroll-behavior:smooth would animate toward each
           sample and never keep up with the pointer */
        track.style.scrollBehavior = 'auto';
        seek(e.clientX);
        e.preventDefault();
      });
      bar.addEventListener('pointermove', function (e) { if (barDown) { seek(e.clientX); } });
      var barUp = function () {
        if (!barDown) { return; }
        barDown = false;
        bar.classList.remove('dtc-railbar--live');
        track.style.scrollBehavior = '';
      };
      bar.addEventListener('pointerup', barUp);
      bar.addEventListener('pointercancel', barUp);
      bar.addEventListener('lostpointercapture', barUp);
    }

    if (prev) { prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }); }); }
    if (next) { next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }); }); }
    track.addEventListener('scroll', update, { passive: true });
    onWidth(update);
    update();

    /* optional autoplay: advance one card, loop back at the end.
       Stops for good once the visitor touches the rail; stays off in
       the theme editor and under reduced-motion. */
    var interval = parseInt(root.getAttribute('data-dtc-autoplay'), 10);
    if (!interval || reduce) { return; }
    if (window.Shopify && window.Shopify.designMode) { return; }
    var stopped = false;
    var hovering = false;
    track.addEventListener('pointerdown', function () { stopped = true; }, { passive: true });
    root.addEventListener('pointerenter', function () { hovering = true; });
    root.addEventListener('pointerleave', function () { hovering = false; });
    var autoTimer = setInterval(function () {
      if (!root.isConnected) { clearInterval(autoTimer); return; }
      if (stopped || hovering || document.hidden || reduce || root.contains(document.activeElement)) { return; }
      var max = track.scrollWidth - track.clientWidth;
      if (max < 6) { return; }
      if (track.scrollLeft >= max - 6) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      }
    }, interval);
  }


  /* ============================================================
     DRAG TO SCROLL - every horizontal rail on the site
     ============================================================
     Touch already pans a rail natively, so this is MOUSE ONLY: a
     pointerdown of type "mouse" grabs the rail, pointermove pushes
     scrollLeft, and the release throws it with inertia before it
     settles on the nearest card. Three details make it feel native
     rather than janky:

       - the rail carries scroll-behavior:smooth for the arrow
         buttons, which would animate every single scrollLeft write
         and lag a full frame behind the cursor, so the drag class
         turns it off and the settle passes behavior:"smooth"
         explicitly instead;
       - scroll-snap-type:x mandatory fights a free drag (the
         browser keeps yanking back to the nearest snap point), so
         the drag class also drops snap and it comes back once the
         throw has landed;
       - a drag that starts on a card ends with a click on that
         card's link, so the first click after a real drag is
         swallowed. Anything under the threshold is still a click,
         which keeps tapping a card working exactly as before.  */
  function initDragScroll(el) {
    if (!el || el.dataset.dtcDrag) { return; }
    el.dataset.dtcDrag = '1';

    var THRESHOLD = 4;      /* px of travel before it counts as a drag, not a click */
    var FRICTION = 0.94;    /* per-frame velocity decay for the throw */
    var down = false, dragging = false, suppress = false;
    var startX = 0, startLeft = 0, lastX = 0, lastT = 0, vel = 0, pid = null, raf = 0;
    var releaseLeft = 0, throwDir = 0;

    function maxScroll() { return el.scrollWidth - el.clientWidth; }
    function stopGlide() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }

    /* The grab cursor is a promise that the rail moves, so it is only worn
       while there is overflow to drag through. */
    var railTabStop = false;
    var grabbable = frameJob(maxScroll, function (max) {
      el.classList.toggle('dtc-draggable', max > 4);
      /* Text-only rails need a tab stop so arrow keys can reach every step.
         Preserve authored tabindex values and remove only the stop we added
         when a wider layout no longer scrolls. */
      if (max > 4 && !el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
        railTabStop = true;
      } else if (max <= 4 && railTabStop) {
        if (el.getAttribute('tabindex') === '0') { el.removeAttribute('tabindex'); }
        railTabStop = false;
      }
    });
    grabbable();
    onWidth(grabbable);
    if ('ResizeObserver' in window) { new ResizeObserver(grabbable).observe(el); }

    function onDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) { return; }
      /* never steal the pointer from a real control inside a card - unless
         the control IS the card (data-dtc-drag-card: the community video
         cards are buttons). A drag over one still scrolls, and the click that
         ends it is swallowed below, so only a real tap plays the video. */
      var ctl = e.target.closest ? e.target.closest('input, select, textarea, button, [data-dtc-no-drag]') : null;
      if (ctl && !ctl.hasAttribute('data-dtc-drag-card')) { return; }
      if (maxScroll() <= 4) { return; }
      stopGlide();
      down = true; dragging = false; suppress = false;
      startX = lastX = e.clientX;
      startLeft = el.scrollLeft;
      lastT = e.timeStamp;
      vel = 0;
    }

    function onMove(e) {
      if (!down || e.pointerType !== 'mouse') { return; }
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) { return; }
        dragging = true;
        el.classList.add('dtc-dragging');
        el.classList.add('dtc-gliding');
        pid = e.pointerId;
        if (el.setPointerCapture) { try { el.setPointerCapture(pid); } catch (err) {} }
      }
      e.preventDefault();
      el.scrollLeft = startLeft - dx;
      var dt = e.timeStamp - lastT;
      if (dt > 0) {
        /* smoothed px/ms so one stuttery frame can't define the throw */
        vel = vel * 0.7 + ((e.clientX - lastX) / dt) * 0.3;
        lastX = e.clientX;
        lastT = e.timeStamp;
      }
    }

    function onUp(e) {
      if (!down) { return; }
      down = false;
      if (pid !== null && el.releasePointerCapture) { try { el.releasePointerCapture(pid); } catch (err) {} }
      pid = null;
      if (!dragging) { return; }
      dragging = false;
      el.classList.remove('dtc-dragging');
      suppress = true;
      /* a pointer that has been still for 90ms is a placement, not a throw */
      if (e && e.timeStamp - lastT > 90) { vel = 0; }
      /* where the finger let go, and which way it was going: settle() may not
         land behind that point (see the note there) */
      releaseLeft = el.scrollLeft;
      throwDir = el.scrollLeft > startLeft ? 1 : (el.scrollLeft < startLeft ? -1 : 0);
      glide();
    }

    /* ONE MOVEMENT PER THROW (audit MOT-05). The rail used to coast first and
       pick a card edge afterwards; once the coast had passed the midpoint the
       nearest allowed edge was behind it, so a forward throw ran BACK up to
       170px to settle. The landing edge is now chosen at release, in the
       direction of the throw, and one time-based ease-out ends exactly on it
       (starting at the release speed, so there is no jolt at the hand-over,
       and the same duration on a 60Hz or a 120Hz screen). */
    function glide() {
      var v = -vel * 16;  /* px per ms -> px per 60fps frame, inverted: drag right scrolls left */
      if (reduce || Math.abs(v) < 0.6) { settle(); return; }
      if (v > 90) { v = 90; } else if (v < -90) { v = -90; }
      var from = el.scrollLeft;
      var to = edgeNear(from + v * FRICTION / (1 - FRICTION), v > 0 ? 1 : -1);
      var dist = to - from;
      if (Math.abs(dist) < 1.5) { el.classList.remove('dtc-gliding'); return; }
      var dur = Math.min(900, Math.max(260, Math.abs(3 * dist * 16.7 / v)));
      var t0 = performance.now();
      raf = requestAnimationFrame(function frame(now) {
        if (reduce) { raf = 0; el.classList.remove('dtc-gliding'); return; }
        var p = Math.min(1, (now - t0) / dur);
        el.scrollLeft = from + dist * (1 - Math.pow(1 - p, 3));
        if (p < 1) { raf = requestAnimationFrame(frame); } else { raf = 0; el.classList.remove('dtc-gliding'); }
      });
    }
    /* the card edge nearest x that does not sit behind the release point */
    function edgeNear(x, dir) {
      var pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
      var edge = el.getBoundingClientRect().left + pad;
      var max = maxScroll(), best = null, bestD = Infinity;
      for (var i = 0; i < el.children.length; i++) {
        var pos = Math.max(0, Math.min(max, el.scrollLeft + el.children[i].getBoundingClientRect().left - edge));
        if (dir > 0 && pos < releaseLeft - 1) { continue; }
        if (dir < 0 && pos > releaseLeft + 1) { continue; }
        if (Math.abs(pos - x) < bestD) { bestD = Math.abs(pos - x); best = pos; }
      }
      return best === null ? Math.max(0, Math.min(max, x)) : best;
    }

    /* Land on a card edge, then hand snapping back to the browser. Measured
       through getBoundingClientRect so it does not care which ancestor happens
       to be the offsetParent.

       NEVER BEHIND THE RELEASE. The nearest edge to a rail of wide cards is
       usually the one the shopper has just dragged past, so a throw forwards
       ended up going backwards (the community rail: released at 500, glided to
       649, snapped back to 267). The edge chosen is the nearest one that does
       not move the rail against the throw; if the throw ran out of cards, the
       nearest edge is used after all. */
    function settle() {
      var kids = el.children;
      if (!kids.length) { el.classList.remove('dtc-gliding'); return; }
      var pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
      var edge = el.getBoundingClientRect().left + pad;
      var best = 0, bestD = Infinity, nearestD = Infinity;
      for (var i = 0; i < kids.length; i++) {
        var d = kids[i].getBoundingClientRect().left - edge;
        if (Math.abs(d) < Math.abs(nearestD)) { nearestD = d; }
        if (throwDir !== 0) {
          var target = el.scrollLeft + d;
          /* dragging the content left scrolls right: a forward throw may only
             land at or beyond where it was released, and the other way round */
          if (throwDir > 0 && target < releaseLeft - 1) { continue; }
          if (throwDir < 0 && target > releaseLeft + 1) { continue; }
        }
        if (Math.abs(d) < Math.abs(bestD)) { bestD = d; best = i; }
      }
      if (bestD === Infinity) { bestD = nearestD; }
      var done = function () { el.classList.remove('dtc-gliding'); };
      if (Math.abs(bestD) < 1.5 || reduce) { done(); return; }
      el.scrollTo({ left: el.scrollLeft + bestD, behavior: 'smooth' });
      /* re-enable snap only once the settle has landed, or it aborts mid-flight */
      if ('onscrollend' in window) {
        el.addEventListener('scrollend', done, { once: true });
        setTimeout(done, 700);
      } else {
        setTimeout(done, 460);
      }
    }

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('lostpointercapture', onUp);
    /* an image or link inside the rail would otherwise start a native
       drag-and-drop ghost the moment the pointer moves */
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    el.addEventListener('click', function (e) {
      if (!suppress) { return; }
      suppress = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    /* a wheel with horizontal intent should cancel an in-flight throw */
    el.addEventListener('wheel', stopGlide, { passive: true });
  }

  /* ============================================================
     BRAND LOGO TRIM - see the note in docs; summary here.
     Logo files are often a wordmark on a square canvas with most of the
     canvas empty. This finds the mark from the pixels and shows only it.
     ============================================================ */
  var LOGO_WELLS = '[data-brand-logo-well], .dtc-brands__logo';

  function logoBox(img) {
    var W = img.naturalWidth, H = img.naturalHeight;
    if (!W || !H) { return null; }
    /* sample small: the bbox only needs to be right to a pixel or two at the
       size it is drawn, and a 240px sample of a 480px logo is 4x cheaper */
    var k = Math.min(1, 240 / Math.max(W, H));
    var sw = Math.max(1, Math.round(W * k)), sh = Math.max(1, Math.round(H * k));
    var cv = document.createElement('canvas');
    cv.width = sw; cv.height = sh;
    var cx = cv.getContext('2d', { willReadFrequently: true });
    var d;
    try {
      cx.drawImage(img, 0, 0, sw, sh);
      d = cx.getImageData(0, 0, sw, sh).data;
    } catch (e) { return null; }

    /* the ground is what the four corners agree on */
    function px(x, y) { var i = (y * sw + x) * 4; return [d[i], d[i + 1], d[i + 2], d[i + 3]]; }
    var corners = [px(0, 0), px(sw - 1, 0), px(0, sh - 1), px(sw - 1, sh - 1)];
    var bg = corners[0];
    var alpha = (corners[0][3] + corners[1][3] + corners[2][3] + corners[3][3]) / 4;
    var transparent = alpha < 24;
    var lum = (bg[0] * 0.299 + bg[1] * 0.587 + bg[2] * 0.114);
    var whiteGround = !transparent && alpha > 200 && lum > 232;
    /* A SOLID PLATE. A logo supplied as small lettering on a solid dark or
       coloured rectangle (Broke Boys: tiny white text in a black box) is
       trimmed to the lettering too, and the well takes the plate's colour, so
       the tile shows the brand's own ground with the mark at a readable size.
       Only when all four corners agree - a gradient or a photograph keeps its
       whole picture. */
    var plate = null;
    if (!transparent && !whiteGround) {
      var agree = corners.every(function (c) {
        return c[3] > 200 && Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]) < 24;
      });
      if (!agree) { return { keep: true }; }
      plate = 'rgb(' + bg[0] + ',' + bg[1] + ',' + bg[2] + ')';
    }

    var minX = sw, minY = sh, maxX = -1, maxY = -1;
    var onN = 0, lumSum = 0;
    for (var y = 0; y < sh; y++) {
      for (var x = 0; x < sw; x++) {
        var i = (y * sw + x) * 4;
        var on;
        if (transparent) {
          on = d[i + 3] > 28;
        } else {
          on = Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2]) > 54;
        }
        if (on) {
          if (transparent) { onN++; lumSum += d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114; }
          if (x < minX) { minX = x; }
          if (x > maxX) { maxX = x; }
          if (y < minY) { minY = y; }
          if (y > maxY) { maxY = y; }
        }
      }
    }
    if (maxX < 0) { return { keep: true }; }
    /* A LIGHT MARK. White lettering on a transparent file (BLO, Car-Chem) is
       invisible on a light well; flushTrims gives such a well the site's dark
       panel colour, as a solid plate gives the well its own colour. */
    var light = transparent && onN > 0 && lumSum / onN > 200;
    var bw = maxX - minX + 1, bh = maxY - minY + 1;
    /* nothing worth trimming: the mark already fills the file */
    if (bw / sw > 0.9 && bh / sh > 0.9) { return { keep: true, light: light }; }
    /* one sample pixel of breathing room so an antialiased edge is not shaved */
    minX = Math.max(0, minX - 1); minY = Math.max(0, minY - 1);
    bw = Math.min(sw - minX, bw + 2); bh = Math.min(sh - minY, bh + 2);
    return { W: W, H: H, l: minX / sw, t: minY / sh, w: bw / sw, h: bh / sh, plate: plate, light: light };
  }

  /* a light mark's well: dark only when its own ground is light (a dark
     scheme's well already shows a white mark) */
  function darkenWell(well) {
    var m = (getComputedStyle(well).backgroundColor || '').match(/[\d.]+/g);
    if (!m) { return; }
    var a = m.length > 3 ? +m[3] : 1;
    var lum = a < 0.2 ? 255 : (+m[0] * 0.299 + +m[1] * 0.587 + +m[2] * 0.114);
    if (lum > 200) {
      well.style.backgroundColor = 'var(--tdhq-charcoal, #151515)';
      well.classList.add('is-plate-logo');
    }
  }

  /* Where the mark goes, split in two so a whole batch of logos can be
     measured in one layout and then placed (see flushTrims): fitLogo only
     READS, placeLogo only WRITES. */
  function fitLogo(img) {
    var box = img.__logoBox;
    if (!box || box.keep) { return null; }
    var clip = img.parentNode;
    if (!clip || !clip.classList || !clip.classList.contains('tdhq-logo-clip')) { return null; }
    var well = clip.parentNode;
    var mW = box.W * box.w, mH = box.H * box.h;          /* mark, natural px */
    var ratio = mW / mH;
    var rw, rh;

    if (img.__logoMode === 'fill') {
      var ws = getComputedStyle(well);
      var innerW = well.clientWidth - parseFloat(ws.paddingLeft) - parseFloat(ws.paddingRight);
      var innerH = well.clientHeight - parseFloat(ws.paddingTop) - parseFloat(ws.paddingBottom);
      if (innerW < 20 || innerH < 12) { return null; }
      /* equal AREA, so a wordmark and a roundel weigh the same. A small inline
         tile asks for a fuller fit through --tdhq-logo-fill; a big brand tile
         keeps the default third. */
      var fill = parseFloat(ws.getPropertyValue('--tdhq-logo-fill')) || 0.3;
      var area = innerW * innerH * fill;
      rh = Math.sqrt(area / ratio);
      rw = rh * ratio;
      if (rw > innerW * 0.92) { rw = innerW * 0.92; rh = rw / ratio; }
      if (rh > innerH * 0.86) { rh = innerH * 0.86; rw = rh * ratio; }
    } else {
      /* HEIGHT: the mark takes the height the stylesheet gave the whole file */
      rh = img.__logoSlotH || 32;
      rw = rh * ratio;
    }
    return { rw: rw, rh: rh, s: rw / mW };                 /* s: rendered px per natural px */
  }

  function placeLogo(img, f) {
    var box = img.__logoBox, clip = img.parentNode;
    clip.style.width = Math.round(f.rw) + 'px';
    clip.style.height = Math.round(f.rh) + 'px';
    img.style.width = Math.round(box.W * f.s) + 'px';
    img.style.height = Math.round(box.H * f.s) + 'px';
    img.style.left = Math.round(-box.W * box.l * f.s) + 'px';
    img.style.top = Math.round(-box.H * box.t * f.s) + 'px';
    sharpenLogo(img, box.W * f.s);
  }

  function layoutLogo(img) {
    var f = fitLogo(img);
    if (f) { placeLogo(img, f); }
  }

  /* A trimmed mark is drawn at the size of its CROP, so the whole file is often
     drawn several times wider than the copy the srcset picked for the tile
     (Broke Boys: a 160px copy drawn 510px wide - blurred lettering). Ask the
     Shopify CDN for the width actually needed, at the screen's density. The
     trim fractions are the same at any resolution, so the layout holds. */
  function sharpenLogo(img, drawnW) {
    var need = Math.min(2000, Math.ceil(drawnW * (window.devicePixelRatio || 1) / 100) * 100);
    /* compare with the width actually requested from the CDN: for an <img> with
       srcset, naturalWidth is density-corrected, so on a 3x phone this test
       nearly always failed and 89 of 114 logos were downloaded a second time,
       85 of them SMALLER than the first (audit perf-06) */
    var haveM = (img.currentSrc || img.src || '').match(/[?&]width=(\d+)/);
    var have = haveM ? +haveM[1] : img.naturalWidth;
    if (!need || need <= have * 1.05 || img.__logoSharpW >= need) { return; }
    var src = img.currentSrc || img.src;
    if (!/[?&]width=\d+/.test(src)) { return; }
    img.__logoSharpW = need;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = src.replace(/([?&]width=)\d+/, '$1' + need);
  }

  /* TRIMS ARE BATCHED, ONE LAYOUT AT A TIME (audit MOT-10). Each logo used to
     be trimmed from its own load event, and each trim forced up to three full
     layouts of the page (its slot height, its well with the image hidden, then
     the fit). On the Brands page, where a lazy grid of 116 logos arrives
     during a scroll, those stacked into long frames of 63-115ms at 390 (4-6
     trims of 13-19ms each). Reading the pixels is cheap (about 0.3ms a logo);
     the layouts were the cost. Logos that finish loading in the same frame are
     now trimmed together on the next animation frame: all reads, then all
     writes, then all fits, then all placements. Whether a kind of well is a
     TILE or sized BY its image is measured once per kind (its class list) and
     remembered, instead of hiding every image to measure its well. */
  var trimQueue = [], trimRaf = 0, wellModes = {};

  function queueTrim(img) {
    trimQueue.push(img);
    if (!trimRaf) { trimRaf = requestAnimationFrame(flushTrims); }
  }

  function flushTrims() {
    if (trimRaf) { cancelAnimationFrame(trimRaf); trimRaf = 0; }
    var batch = trimQueue;
    trimQueue = [];
    var jobs = [];
    batch.forEach(function (img) {
      if (img.__logoDone) { return; }
      var well = img.closest(LOGO_WELLS);
      if (!well) { return; }
      var box = logoBox(img);
      if (!box) { return; }                                /* unreadable: leave it */
      img.__logoDone = true;
      img.__logoBox = box;
      if (box.light) { darkenWell(well); }
      if (!box.keep) { jobs.push({ img: img, well: well, box: box, key: well.className }); }
    });
    if (!jobs.length) { return; }

    /* READ: every slot height, in one layout */
    jobs.forEach(function (j) { j.slotH = j.img.getBoundingClientRect().height; });

    /* A kind of well not seen yet: hide those images together, read the
       wells, restore. A well that holds its size without the image is a TILE;
       one that collapses is sized BY the image. Its CONTENT size counts:
       padding and borders alone are not a tile. A width threshold once misread
       the phone's 56px collection thumbnail as a text line and cropped
       AUTOBEAD to "OB"; a well whose height is only its image (the product
       page's brand band) has no height of its own and is sized by height. */
    var probe = jobs.filter(function (j) { return !Object.prototype.hasOwnProperty.call(wellModes, j.key); });
    probe.forEach(function (j) { j.prev = j.img.style.display; j.img.style.display = 'none'; });
    probe.forEach(function (j) {
      var wcs = getComputedStyle(j.well), r = j.well.getBoundingClientRect();
      var emptyW = r.width - parseFloat(wcs.paddingLeft) - parseFloat(wcs.paddingRight) -
        parseFloat(wcs.borderLeftWidth) - parseFloat(wcs.borderRightWidth);
      var emptyH = r.height - parseFloat(wcs.paddingTop) - parseFloat(wcs.paddingBottom) -
        parseFloat(wcs.borderTopWidth) - parseFloat(wcs.borderBottomWidth);
      var mode = emptyW > 16 && emptyH >= 12 ? 'fill' : 'height';
      /* a well hidden by a filter measures 0 wide; one with its own aspect
         ratio is still a tile */
      if (mode === 'height' && wcs.aspectRatio && wcs.aspectRatio !== 'auto') { mode = 'fill'; }
      j.mode = mode;
      j.seen = r.width > 0;                                /* only a drawn well teaches its kind */
    });
    probe.forEach(function (j) {
      j.img.style.display = j.prev;
      if (j.seen && !Object.prototype.hasOwnProperty.call(wellModes, j.key)) { wellModes[j.key] = j.mode; }
    });

    /* WRITE: each mark goes into its clip */
    jobs.forEach(function (j) {
      var img = j.img;
      img.__logoMode = j.mode || wellModes[j.key];
      img.__logoSlotH = j.slotH;
      var clip = document.createElement('span');
      clip.className = 'tdhq-logo-clip';
      img.parentNode.insertBefore(clip, img);
      clip.appendChild(img);
      img.classList.add('tdhq-logo-trimmed');
      if (j.box.plate) {
        clip.parentNode.style.backgroundColor = j.box.plate;
        clip.parentNode.classList.add('is-plate-logo');
      }
    });

    /* READ every fit, then WRITE every placement */
    var fits = jobs.map(function (j) { return fitLogo(j.img); });
    jobs.forEach(function (j, i) { if (fits[i]) { placeLogo(j.img, fits[i]); } });
  }

  function initLogoTrim(scope) {
    var imgs = (scope || document).querySelectorAll(
      '[data-brand-logo-well] img, .dtc-brands__logo img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.complete && img.naturalWidth) { queueTrim(img); }
      else { img.addEventListener('load', function () { queueTrim(img); }, { once: true }); }
    });
    /* the logos already loaded are trimmed now, as before, not a frame
       later: one batch for all of them */
    flushTrims();
    if (!initLogoTrim._bound) {
      initLogoTrim._bound = true;
      /* tiles change width with the viewport, so FILL marks re-fit - all
         measured first, then all placed */
      onWidth(coalesced(function () {
        var done = Array.prototype.filter.call(document.querySelectorAll('img.tdhq-logo-trimmed'), function (im) { return im.__logoMode === 'fill'; });
        var fits = done.map(fitLogo);
        done.forEach(function (im, i) { if (fits[i]) { placeLogo(im, fits[i]); } });
      }));
    }
  }

  /* Every horizontal rail on the site, in one list. A rail that is missing
     from it behaves differently from its neighbours, which is exactly what
     the client keeps reporting. */
  var RAIL_SELECTOR = '.dtc-track--slider, .dtc-grid--rail, [data-dtc-drag],' +
    '.tdhq-cards--rail, .tdhq-cards--rail-phone, .dtc-blog__grid--rail,' +
    '.tdhq-artrel__grid, .tdhq-related, .tdhq-brandcat__rail, .tdhq-fbrands__track, .tdhq-steps,' +
    '.dtc-icongrid__row, .dtc-cart__recslist';
  /* NOT rails, and deliberately left alone: the announcement marquee
     (.tdhq-announce__track, which scrolls itself), the hero slider
     (.dtc-hero__track, its own arrows and dots) and the product gallery
     thumbnails (.dtc-pdp__thumbs, driven by the stage arrows). */

  function initDragRails(scope) {
    var root = scope || document;
    root.querySelectorAll(RAIL_SELECTOR).forEach(initDragScroll);
    root.querySelectorAll(RAIL_SELECTOR).forEach(initRailAffordance);
    /* A card grid can also become a phone rail in CSS alone: the store
       information and "What you will find" cards, Contact's ways to reach us
       and Track an Order's common problems are made rails by their section ids
       in tdhq-fix-service.css and carry no rail class. At 390 they scrolled
       sideways with a card cut at the edge and nothing saying the row moves,
       beside rails that had the bar and arrows (2026-09-23). They are found by
       what they do - scroll sideways - and get the same drag and footer. */
    root.querySelectorAll('.tdhq-cards').forEach(function (el) {
      var ox = window.getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') { initDragScroll(el); initRailAffordance(el); }
    });
  }

  /* THE SAME PULL AFFORDANCE ON EVERY RAIL. The product rails ship a footer
     with a position bar and a pair of arrows; nine other rails shipped
     nothing, so a row that can be dragged looked like a row that was simply
     cut off. The footer is built here, from the same classes, for any rail
     that scrolls and does not already have one (a carousel section builds its
     own, and is left alone). */
  function initRailAffordance(track) {
    if (!track || track.dataset.dtcRailUi) { return; }
    if (track.closest('[data-dtc-carousel]')) { return; }
    var host = track.parentElement;
    if (!host || host.querySelector('[data-dtc-railbar]')) { return; }
    track.dataset.dtcRailUi = '1';

    var foot = document.createElement('div');
    foot.className = 'dtc-railfoot dtc-railfoot--auto';
    var bar = document.createElement('div');
    bar.className = 'dtc-railbar';
    bar.setAttribute('data-dtc-railbar', '');
    var thumb = document.createElement('span');
    thumb.className = 'dtc-railbar__thumb';
    bar.appendChild(thumb);
    var nav = document.createElement('div');
    nav.className = 'dtc-slider__nav dtc-carousel__nav';
    function arrow(dir, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dtc-slider__btn';
      b.setAttribute('aria-label', label);
      /* the same arrow every section-built rail draws (layout/theme.liquid holds
         it in a template); the hand-drawn chevron is only the fallback */
      var tpl = document.getElementById('dtc-rail-arrow');
      if (tpl && tpl.content && tpl.content.firstElementChild) {
        var ic = tpl.content.firstElementChild.cloneNode(true);
        if (dir < 0) { ic.classList.add('dtc-flip'); }
        b.appendChild(ic);
      } else {
        b.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">' +
          '<path d="' + (dir < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      b.addEventListener('click', function () {
        var card = track.children[0];
        var step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * step, behavior: reduce ? 'auto' : 'smooth' });
      });
      return b;
    }
    var prev = arrow(-1, 'Previous');
    var next = arrow(1, 'Next');
    nav.appendChild(prev);
    nav.appendChild(next);
    foot.appendChild(bar);
    foot.appendChild(nav);
    if (track.nextSibling) { host.insertBefore(foot, track.nextSibling); } else { host.appendChild(foot); }

    var paint = frameJob(function () {
      return { sw: track.scrollWidth, cw: track.clientWidth, left: track.scrollLeft, barW: bar.clientWidth };
    }, function (m) {
      var max = m.sw - m.cw;
      var wasHidden = foot.hidden;
      foot.hidden = max <= 4;
      if (max <= 4) { return; }
      /* the bar was measured inside a hidden footer: measure it again shown */
      if (wasHidden) { paint(); return; }
      var w = Math.max(28, Math.round(m.barW * (m.cw / m.sw)));
      thumb.style.width = w + 'px';
      thumb.style.transform = 'translateX(' + Math.round((m.barW - w) * Math.min(1, Math.max(0, m.left / max))) + 'px)';
      prev.disabled = m.left <= 2;
      next.disabled = m.left >= max - 2;
    });
    track.addEventListener('scroll', paint, { passive: true });
    onWidth(paint);
    if ('ResizeObserver' in window) { new ResizeObserver(paint).observe(track); }
    paint();
  }

  /* ---- hero slider: arrows, dots, optional autoplay. Autoplay
     pauses on hover/touch and hidden tabs, and stays off in the
     theme editor and under reduced-motion. ---- */
  function initHeroSlider(root) {
    if (!root || root.dataset.dtcHero) { return; }
    root.dataset.dtcHero = '1';
    var track = root.querySelector('[data-dtc-hero-track]');
    if (!track) { return; }

    /* The pictures for slides 2 and up carry their source in data attributes
       so they are not fetched while the FIRST slide - the largest thing on
       the page - is still arriving. They are filled in at the visitor's first
       touch, scroll, key or mouse move (21 Oct, speed: a page nobody has touched
       does not fetch two more large pictures while it loads), and carry their
       size so the browser reserves the box. */
    var laterSlots = Array.prototype.slice.call(root.querySelectorAll('[data-dtc-hero-img][data-src]'));
    function loadLaterSlides() {
      if (!laterSlots.length) { return; }
      laterSlots.forEach(function (slot) {
        var img = document.createElement('img');
        img.className = 'dtc-hero__img';
        img.alt = slot.getAttribute('data-alt') || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        if (slot.getAttribute('data-w') && slot.getAttribute('data-h')) {
          img.width = parseInt(slot.getAttribute('data-w'), 10);
          img.height = parseInt(slot.getAttribute('data-h'), 10);
        }
        if (slot.getAttribute('data-sizes')) { img.sizes = slot.getAttribute('data-sizes'); }
        if (slot.getAttribute('data-srcset')) { img.srcset = slot.getAttribute('data-srcset'); }
        img.src = slot.getAttribute('data-src');
        slot.parentNode.replaceChild(img, slot);
      });
      laterSlots = [];
    }
    /* the visitor's first touch, scroll, key or mouse move anywhere on the page, once */
    var woke = false, wakeFns = [];
    var wakeEvents = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'scroll'];
    function wake() {
      if (woke) { return; }
      woke = true;
      wakeEvents.forEach(function (ev) { window.removeEventListener(ev, wake, true); });
      wakeFns.forEach(function (fn) { fn(); });
    }
    wakeEvents.forEach(function (ev) { window.addEventListener(ev, wake, { capture: true, passive: true }); });
    wakeFns.push(loadLaterSlides);
    var prev = root.querySelector('[data-dtc-hero-prev]');
    var next = root.querySelector('[data-dtc-hero-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-dtc-hero-dot]'));
    var count = track.children.length;

    function index() {
      return Math.round(track.scrollLeft / track.clientWidth);
    }

    function go(i) {
      var target = Math.max(0, Math.min(count - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    }

    var update = frameJob(index, function (i) {
      dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
    });

    if (prev) { prev.addEventListener('click', function () { go(index() - 1); }); }
    if (next) { next.addEventListener('click', function () { go(index() + 1); }); }
    dots.forEach(function (d) {
      d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-dtc-hero-dot'), 10)); });
    });
    track.addEventListener('scroll', update, { passive: true });
    onWidth(update);
    update();

    var interval = parseInt(root.getAttribute('data-dtc-hero-autoplay'), 10);
    if (!interval || reduce) { return; }
    if (window.Shopify && window.Shopify.designMode) { return; }
    var paused = false;
    root.addEventListener('pointerenter', function () { paused = true; });
    root.addEventListener('pointerleave', function () { paused = false; });
    root.addEventListener('pointerdown', function () { paused = true; }, { passive: true });
    /* rotation starts with the visitor's first touch, scroll, key or mouse move (21 Oct, speed) */
    function startAuto() {
      var heroTimer = setInterval(function () {
        if (!root.isConnected) { clearInterval(heroTimer); return; }
        if (paused || document.hidden || reduce || root.contains(document.activeElement)) { return; }
        var i = index();
        go(i >= count - 1 ? 0 : i + 1);
      }, interval);
    }
    if (woke) { startAuto(); } else { wakeFns.push(startAuto); }
  }

  /* ---- FAQ groups: smooth animated open/close. Several answers may be open at
     once by default, the rule every FAQ on the site follows (the FAQ page, the
     help-page FAQs; client: "in some parts if you open one faq another will close
     in another part of the website you can open all of them"). A section can opt
     into one-at-a-time with data-dtc-acc-single="true". The product page FAQ is
     one-at-a-time (client 2026-09-25: "if you open one another should close").
     Closing the open sibling instantly made the tapped question jump 84px up
     before it opened (2026-09-23), so initAccordion holds the tapped question
     where it was: it measures it before and after and scrolls the difference.
     Reduced-motion + no-JS fall back to the native instant toggle. The height is animated in JS (native <details> can't tween
     auto), the +/x mark rotation is CSS. ---- */
  function accCancel(d) { if (d._accAnim) { try { d._accAnim.cancel(); } catch (e) {} d._accAnim = null; } }
  function accExpand(d, panel) {
    accCancel(d);
    d.dataset.accAnim = '1';
    d.open = true;                 /* must be open to render + measure */
    var target = panel.scrollHeight;
    if (!panel.animate) { delete d.dataset.accAnim; return; }
    var a = panel.animate(
      [{ height: '0px', opacity: 0 }, { height: target + 'px', opacity: 1 }],
      { duration: (window.TdhqMotion ? window.TdhqMotion.ms('--tdhq-t', 220) : 220), easing: (window.TdhqMotion ? window.TdhqMotion.easing() : 'cubic-bezier(0.4, 0, 0.2, 1)') }
    );
    d._accAnim = a;
    a.onfinish = function () { d._accAnim = null; delete d.dataset.accAnim; };
    a.oncancel = function () { delete d.dataset.accAnim; };
  }
  function accCollapse(d, panel) {
    if (!panel) { d.open = false; return; }
    accCancel(d);
    d.dataset.accAnim = '1';
    var start = panel.offsetHeight;
    if (!panel.animate) { d.open = false; delete d.dataset.accAnim; return; }
    var a = panel.animate(
      [{ height: start + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: (window.TdhqMotion ? window.TdhqMotion.ms('--tdhq-t', 220) : 220), easing: (window.TdhqMotion ? window.TdhqMotion.easing() : 'cubic-bezier(0.4, 0, 0.2, 1)') }
    );
    d._accAnim = a;
    a.onfinish = function () { d.open = false; d._accAnim = null; delete d.dataset.accAnim; };
    a.oncancel = function () { delete d.dataset.accAnim; };
  }
  function accReset(o) { /* instant-close a sibling so only the clicked item animates */
    accCancel(o);
    o.open = false;
    delete o.dataset.accAnim;
  }
  function initAccordion(group) {
    if (!group || group.dataset.dtcAcc) { return; }
    group.dataset.dtcAcc = '1';
    var single = group.getAttribute('data-dtc-acc-single') === 'true';
    /* data-dtc-acc-inplace (U.60, the product FAQ beside HQ Suggests): the page never scrolls, so nothing beside
       the list moves. An open answer ON SCREEN closes in place with its animation; one OFF SCREEN is left open,
       because closing it unseen is what forced the page to move. Still one answer open in view. */
    var inplace = group.hasAttribute('data-dtc-acc-inplace');
    function onScreen(o) {
      var r = o.getBoundingClientRect();
      var top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tdhq-sticky-h')) || 0;
      return r.bottom > top && r.top < window.innerHeight;
    }
    group.querySelectorAll('details.dtc-acc__item').forEach(function (d) {
      var summary = d.querySelector('summary');
      var panel = d.querySelector('.dtc-acc__a');
      if (!summary || !panel) { return; }
      /* CLAIM IT. initDisclosures() binds its own open/close handler to any
         <details> not carrying this attribute; with two handlers on one
         summary the second reads the state the first just set and closes the
         item again. This item has a controller - hands off. */
      d.setAttribute('data-dtc-disc', '1');
      summary.addEventListener('click', function (e) {
        if (reduce) { /* let the native toggle run; still enforce single-open */
          if (!d.open && single) {
            var ry = summary.getBoundingClientRect().top, rshown = false;
            group.querySelectorAll('details.dtc-acc__item[open]').forEach(function (o) {
              if (o === d) { return; }
              if (inplace) { rshown = true; if (onScreen(o)) { o.open = false; } return; }
              o.open = false;
            });
            var rdy = summary.getBoundingClientRect().top - ry;
            if (rdy && !rshown) { window.scrollBy({ top: rdy, behavior: 'instant' }); }
          }
          return;
        }
        e.preventDefault();
        if (d.dataset.accAnim) { return; }
        if (d.open) {
          accCollapse(d, panel);
        } else {
          /* single-open: close the others INSTANTLY. Animating a collapse and an
             expand at the same time reflows the whole list every frame and makes
             the open item visibly stumble - one animation at a time is smooth. */
          if (single) {
            var y0 = summary.getBoundingClientRect().top, shown = false;
            group.querySelectorAll('details.dtc-acc__item[open]').forEach(function (o) {
              if (o === d) { return; }
              if (inplace) { shown = true; if (onScreen(o)) { accCollapse(o, o.querySelector('.dtc-acc__a')); } return; }
              accReset(o);
            });
            var dy = summary.getBoundingClientRect().top - y0;
            if (dy && !shown) { window.scrollBy({ top: dy, behavior: 'instant' }); }
          }
          accExpand(d, panel);
        }
      });
    });
  }

  /* ---- scroll reveal: fade below-fold blocks up as they enter.
     Hidden state exists only under html.dtc-reveal-on, so no-JS
     never hides content. No blanket timer fail-safe: a timer that
     pre-reveals off-screen sections kills the animation for anyone
     who pauses before scrolling; the capability guards below are
     the safety net. Theme editor always shows everything. ---- */
  function initReveal() {
    if (reduce || !('IntersectionObserver' in window)) { return; }
    /* NOT IN THE PHONE LAYOUT (client, 25 Sep 2026: "animations scroll down and
       up is terrible"). Scrolling a phone at thumb speed, whole sections arrived
       blank and faded in behind the finger: up to 74% of the screen blank or
       mid-fade on the home page (vis/phone-blank.cjs). Up to 899px, the phone
       layout's breakpoint, content is simply there. */
    if (window.matchMedia && window.matchMedia('(max-width: 899px)').matches) { return; }
    if (window.__dtcAnimOff) { return; }
    if (window.Shopify && window.Shopify.designMode) { return; }
    /* EVERY SECTION REVEALS, not only those whose markup opted in (audit
       REQ-ANIM-01: the tdhq- sections never did). A section already holding
       its own reveal or entrance keeps it; one already on screen at load is
       left exactly as it is by the pass below. */
    document.querySelectorAll('#MainContent > .shopify-section').forEach(function (s) {
      if (s.querySelector('.dtc-reveal, [data-dtc-appear]')) { return; }
      var f = s.firstElementChild;
      while (f && /^(SCRIPT|STYLE|TEMPLATE|LINK)$/.test(f.tagName)) { f = f.nextElementSibling; }
      if (f && !f.matches('dialog, [hidden]')) { f.classList.add('dtc-reveal'); }
    });
    var all = document.querySelectorAll('.dtc-reveal:not([data-dtc-revealed])');
    if (!all.length) { return; }
    /* A CARD INSIDE A HORIZONTAL RAIL SCROLLS SIDEWAYS, NOT DOWN, and this
       observer watches the VIEWPORT: a card past the rail's right edge never
       intersects it, so it stayed at opacity 0 for the life of the page and
       then faded in under the finger as soon as the rail was dragged
       (measured on the homepage: 22 of 80 reveal elements, four rails). A
       card in a rail is marked done and left visible; the rail itself may
       still reveal as a whole. */
    /* A BAND WITH ITS OWN DARK COLOUR IS SIMPLY THERE (21 Oct 2026). Faded from opacity 0, a black band let the
       white page ground show through and flashed white before going dark. Light bands still fade in over the
       white ground. Colours read for every element first, then the writes below. */
    var dark = Array.prototype.map.call(all, function (el) {
      var m = (window.getComputedStyle(el).backgroundColor || '').match(/[\d.]+/g);
      if (!m || m.length < 3 || (m.length > 3 && Number(m[3]) < 0.5)) { return false; }
      return 0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2] < 128;
    });
    var els = Array.prototype.filter.call(all, function (el, i) {
      if (dark[i]) {
        el.setAttribute('data-dtc-revealed', '1');
        el.classList.add('is-in');
        return false;
      }
      var rail = el.parentElement && el.parentElement.closest
        ? el.parentElement.closest('.dtc-track--slider, .dtc-grid--rail, [data-dtc-track]')
        : null;
      if (!rail) { return true; }
      el.setAttribute('data-dtc-revealed', '1');
      el.classList.add('is-in');
      return false;
    });
    if (!els.length) { return; }
    document.documentElement.classList.add('dtc-reveal-on');
    /* reuse ONE observer across re-inits (shopify:section:load) so we don't leak a new one each time */
    if (!initReveal._io) {
      initReveal._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) { return; }
          var el = entry.target;
          el.classList.add('is-in');
          initReveal._io.unobserve(el);
          /* AND THEN LET GO. While .is-pending is on the element it owns the
             whole `transition` shorthand (opacity and transform, 550ms), so
             the card's own hover lift never ran - every card carrying
             .dtc-reveal measured 0px of lift at 550ms instead of 2-3px at
             160-220ms. Once the entrance has played the class comes off and
             the element goes back to being an ordinary card. */
          var release = function () { el.classList.remove('is-pending'); };
          el.addEventListener('transitionend', release, { once: true });
          window.setTimeout(release, 900);
        });
      }, { rootMargin: '0px 0px -10% 0px' });
    }
    var io = initReveal._io;
    /* Two passes on purpose: adding .is-in sets `transform: none`, and getBoundingClientRect
       reflects transforms — so interleaving the reads and writes would force a synchronous
       layout flush per element. Read every rect first, then write. */
    var vh = window.innerHeight;
    var rects = [];
    els.forEach(function (el) { rects.push(el.getBoundingClientRect()); });
    els.forEach(function (el, i) {
      el.setAttribute('data-dtc-revealed', '1');
      var r = rects[i];
      /* ALREADY ON SCREEN: leave it exactly as it is. It is not given the
         pending class, so it is never hidden and never transitions - the
         shopper is looking straight at it. */
      if (r.top < vh && r.bottom > 0) {
        el.classList.add('is-in');
      } else {
        /* Below the fold: hide it, then reveal it when it is scrolled to. */
        el.classList.add('is-pending');
        io.observe(el);
      }
    });
  }

  /* The enquiry popup's opener, focus trap and booking submit were removed
     on 2026-09-23 (build report DC-11): the popup's sections were deleted and
     #dtc-enquiry exists on no page. */
  /* ---- back to top: appears after scrolling, smooth return ---- */
  function initBackToTop() {
    if (document.body.dataset.dtcTop) { return; }
    document.body.dataset.dtcTop = '1';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dtc-backtotop';
    btn.setAttribute('aria-label', 'Back to top');
    document.body.appendChild(btn);
    var check = frameJob(function () { return window.scrollY; }, function (y) {
      btn.classList.toggle('is-visible', y > 600);
    });
    window.addEventListener('scroll', check, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    check();
  }

  /* ---- tap-to-unmute for autoplaying videos. Only the button
     itself toggles sound - the click never bubbles into the tile,
     so it cannot open the lightbox or anything else. ---- */
  function initVideoSound() {
    if (document.body.dataset.dtcVideoSoundInit) { return; }
    document.body.dataset.dtcVideoSoundInit = '1';
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-dtc-video-sound]');
      if (!btn) { return; }
      e.preventDefault();
      e.stopPropagation();
      var scope = btn.parentElement;
      var video = scope ? scope.querySelector('video') : null;
      if (!video) { return; }
      video.muted = !video.muted;
      btn.classList.toggle('is-on', !video.muted);
      if (!video.muted) { video.play().catch(function () {}); }
    }, true);
  }

  /* ---- viewport-driven video playback. Our videos ship with NO
     autoplay and preload="none" so they never touch the network until
     they are actually near the screen (this is what stops video-heavy
     pages hanging/spinning on load). Here we play them only while
     visible and pause + free the network when they scroll away. ---- */
  function initVideoPlayback() {
    if (document.body.dataset.dtcVideoPlayInit) { return; }
    document.body.dataset.dtcVideoPlayInit = '1';
    var videos = document.querySelectorAll('video.dtc-video');
    if (!videos.length) { return; }
    if (!('IntersectionObserver' in window)) {
      if (!reduce) { videos.forEach(function (v) { try { v.play().catch(function () {}); } catch (e) {} }); }
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          if (v.getAttribute('preload') === 'none') { v.preload = 'metadata'; }
          if (reduce) { return; /* reduced motion: load a still frame, no playback */ }
          if (v.readyState === 0) { v.load(); }
          v.play().catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '150px 0px', threshold: 0.2 });
    videos.forEach(function (v) { io.observe(v); });
  }

  /* ---- live Instagram feed (Graph API). When the section has a
     token, fetch the latest posts and reels and replace the curated
     tiles; reels render as muted looping videos. Tiles are built
     with DOM methods (no HTML interpolation). Any failure keeps the
     curated tiles - never a broken section. ---- */
  var IG_GLYPH = '<rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>';

  function igTile(post) {
    var a = document.createElement('a');
    a.className = 'dtc-insta__item';
    a.href = post.permalink;
    a.target = '_blank';
    a.rel = 'noopener';
    a.setAttribute('aria-label', 'View on Instagram');
    var media;
    if (post.media_type === 'VIDEO') {
      media = document.createElement('video');
      media.src = post.media_url;
      if (post.thumbnail_url) { media.poster = post.thumbnail_url; }
      media.muted = true;
      media.autoplay = true;
      media.loop = true;
      media.playsInline = true;
      media.preload = 'metadata';
    } else {
      media = document.createElement('img');
      media.src = post.media_url;
      media.alt = 'DTC on Instagram';
      media.loading = 'lazy';
    }
    a.appendChild(media);
    var overlay = document.createElement('span');
    overlay.className = 'dtc-insta__overlay';
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '26');
    svg.setAttribute('height', '26');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.innerHTML = IG_GLYPH; /* constant markup, not user data */
    overlay.appendChild(svg);
    a.appendChild(overlay);
    return a;
  }

  function initInstaFeed() {
    document.querySelectorAll('[data-dtc-ig-token]').forEach(function (grid) {
      if (grid.dataset.dtcIgDone) { return; }
      grid.dataset.dtcIgDone = '1';
      var token = grid.getAttribute('data-dtc-ig-token');
      var limit = parseInt(grid.getAttribute('data-dtc-ig-limit'), 10) || 8;
      if (!token) { return; }
      var url = 'https://graph.instagram.com/me/media?fields=id,media_type,media_url,thumbnail_url,permalink&limit=' + limit + '&access_token=' + encodeURIComponent(token);
      fetch(url)
        .then(function (r) { if (!r.ok) { throw new Error('IG ' + r.status); } return r.json(); })
        .then(function (data) {
          if (!data.data || !data.data.length) { return; }
          var frag = document.createDocumentFragment();
          data.data.slice(0, limit).forEach(function (post) {
            if (!post.media_url || !post.permalink) { return; }
            frag.appendChild(igTile(post));
          });
          if (frag.childNodes.length) {
            grid.textContent = '';
            grid.appendChild(frag);
          }
        })
        .catch(function (err) {
          if (window.console) { console.warn('DTC Instagram feed unavailable:', err.message); }
        });
    });
  }

  /* ---- gallery lightbox: click any tile to view it full size with
     left/right navigation, keyboard arrows and captions. Videos play
     with controls. "View full gallery" buttons open at the first
     item. Built once, reused by every gallery on the page. ---- */
  var lightbox = null;

  function buildLightbox() {
    if (lightbox) { return lightbox; }
    var root = document.createElement('div');
    root.className = 'dtc dtc-lightbox';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Gallery viewer');

    var backdrop = document.createElement('div');
    backdrop.className = 'dtc-lightbox__backdrop';
    var stage = document.createElement('div');
    stage.className = 'dtc-lightbox__stage';
    var media = document.createElement('div');
    media.className = 'dtc-lightbox__media';
    var caption = document.createElement('div');
    caption.className = 'dtc-lightbox__caption';
    var count = document.createElement('span');
    count.className = 'dtc-lightbox__count';

    function navBtn(cls, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dtc-lightbox__btn ' + cls;
      b.setAttribute('aria-label', label);
      return b;
    }
    var prev = navBtn('dtc-lightbox__btn--prev', 'Previous');
    var next = navBtn('dtc-lightbox__btn--next', 'Next');
    var close = navBtn('dtc-lightbox__btn--close', 'Close');

    stage.appendChild(media);
    stage.appendChild(caption);
    root.appendChild(backdrop);
    root.appendChild(stage);
    root.appendChild(prev);
    root.appendChild(next);
    root.appendChild(close);
    root.appendChild(count);
    document.body.appendChild(root);

    var items = [];
    var index = 0;

    function show(i) {
      if (!items.length) { return; }
      index = (i + items.length) % items.length;
      var item = items[index];
      media.textContent = '';
      var el;
      if (item.video) {
        el = document.createElement('video');
        var src = document.createElement('source');
        src.src = item.video;
        el.appendChild(src);
        el.controls = true;
        el.autoplay = true;
        el.playsInline = true;
        /* Muted: opening a gallery item never switches sound on by itself
           (client: videos "play without sound and if you want you can turn
           it on"; REQ-VID-01). The controls carry the sound. */
        el.muted = true;
      } else {
        el = document.createElement('img');
        el.src = item.src;
        el.alt = item.title || 'Gallery image';
      }
      media.appendChild(el);
      caption.textContent = '';
      if (item.accent) {
        var a = document.createElement('span');
        a.className = 'dtc-lightbox__accent';
        a.textContent = item.accent;
        caption.appendChild(a);
      }
      if (item.title) {
        var t = document.createElement('span');
        t.className = 'dtc-lightbox__title';
        t.textContent = item.title;
        caption.appendChild(t);
      }
      if (item.text) {
        var x = document.createElement('span');
        x.className = 'dtc-lightbox__text';
        x.textContent = item.text;
        caption.appendChild(x);
      }
      count.textContent = (index + 1) + ' / ' + items.length;
      prev.hidden = next.hidden = items.length < 2;
    }

    function openAt(list, i) {
      items = list;
      root.hidden = false;
      document.documentElement.classList.add('dtc-lightbox-open');
      show(i);
    }

    function shut() {
      root.hidden = true;
      media.textContent = ''; /* stops any playing video */
      document.documentElement.classList.remove('dtc-lightbox-open');
    }

    prev.addEventListener('click', function () { show(index - 1); });
    next.addEventListener('click', function () { show(index + 1); });
    close.addEventListener('click', shut);
    backdrop.addEventListener('click', shut);
    document.addEventListener('keydown', function (e) {
      if (root.hidden) { return; }
      if (e.key === 'Escape') { shut(); }
      if (e.key === 'ArrowLeft') { show(index - 1); }
      if (e.key === 'ArrowRight') { show(index + 1); }
    });

    lightbox = { openAt: openAt, shut: shut };
    return lightbox;
  }

  function galleryItems(gallery) {
    var list = [];
    gallery.querySelectorAll('.dtc-gallery__item:not([hidden])').forEach(function (fig) {
      var m = fig.querySelector('.dtc-gallery__media');
      if (!m) { return; }
      var video = m.querySelector('video source');
      var img = m.querySelector('img');
      list.push({
        video: video ? video.src : null,
        src: fig.getAttribute('data-dtc-full') || (img ? (img.currentSrc || img.src) : ''),
        accent: fig.getAttribute('data-dtc-accent') || '',
        title: fig.getAttribute('data-dtc-title') || '',
        text: fig.getAttribute('data-dtc-text') || ''
      });
    });
    return list;
  }

  /* ---- service page media: thumbnails swap the main image ---- */
  function initServiceMedia() {
    if (document.body.dataset.dtcServiceMediaInit) { return; }
    document.body.dataset.dtcServiceMediaInit = '1';

    function setActive(gallery, idx) {
      var slides = gallery.querySelectorAll('[data-dtc-slide]');
      if (!slides.length) { return; }
      idx = ((idx % slides.length) + slides.length) % slides.length;
      slides.forEach(function (s) { s.classList.toggle('is-active', parseInt(s.getAttribute('data-dtc-slide'), 10) === idx); });
      gallery.querySelectorAll('[data-dtc-thumb]').forEach(function (t) { t.classList.toggle('is-active', parseInt(t.getAttribute('data-dtc-thumb'), 10) === idx); });
      var cur = gallery.querySelector('[data-dtc-gcur]');
      if (cur) { cur.textContent = idx + 1; }
      gallery.setAttribute('data-dtc-idx', idx);
      var at = gallery.querySelector('[data-dtc-thumb].is-active');
      if (at && at.scrollIntoView) { at.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: window.tdhqMotion.behavior() }); }
    }

    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-dtc-thumb]');
      var prev = e.target.closest('[data-dtc-gprev]');
      var next = e.target.closest('[data-dtc-gnext]');
      if (!thumb && !prev && !next) { return; }
      var gallery = e.target.closest('[data-dtc-gallery]');
      if (!gallery) { return; }
      e.preventDefault();
      e.stopPropagation();
      var idx = parseInt(gallery.getAttribute('data-dtc-idx') || '0', 10);
      if (thumb) { idx = parseInt(thumb.getAttribute('data-dtc-thumb'), 10); }
      else if (prev) { idx = idx - 1; }
      else { idx = idx + 1; }
      setActive(gallery, idx);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') { return; }
      var gallery = document.querySelector('[data-dtc-gallery]:hover');
      if (!gallery) { return; }
      var idx = parseInt(gallery.getAttribute('data-dtc-idx') || '0', 10);
      setActive(gallery, e.key === 'ArrowLeft' ? idx - 1 : idx + 1);
    });
  }

  function initGalleries() {
    if (document.body.dataset.dtcGalleryInit) { return; }
    document.body.dataset.dtcGalleryInit = '1';

    document.addEventListener('click', function (e) {
      /* photo/video filter chips */
      var fbtn = e.target.closest('[data-dtc-filter]');
      if (fbtn) {
        var gallery = fbtn.closest('.dtc-gallery');
        if (!gallery) { return; }
        var want = fbtn.getAttribute('data-dtc-filter');
        gallery.querySelectorAll('[data-dtc-filter]').forEach(function (b) {
          b.classList.toggle('is-active', b === fbtn);
        });
        gallery.querySelectorAll('.dtc-gallery__item').forEach(function (fig) {
          fig.hidden = want !== 'all' && fig.getAttribute('data-dtc-type') !== want;
        });
        return;
      }

      /* "view full gallery" opens the lightbox at the first item */
      var allBtn = e.target.closest('[data-dtc-gallery-all]');
      if (allBtn) {
        var g = allBtn.closest('.dtc-gallery');
        if (g) {
          e.preventDefault();
          buildLightbox().openAt(galleryItems(g), 0);
        }
        return;
      }

      /* quote tiles (tinting fleet) open the enquiry popup instead */
      if (e.target.closest('.dtc-gallery__item[data-dtc-enquiry]')) { return; }

      /* any other tile opens the lightbox at its own position */
      var figClick = e.target.closest('.dtc-gallery__item');
      if (figClick && !e.target.closest('a')) {
        var gal = figClick.closest('.dtc-gallery');
        if (!gal || gal.getAttribute('data-dtc-lightbox') === 'off') { return; }
        var figs = [].slice.call(gal.querySelectorAll('.dtc-gallery__item:not([hidden])'));
        var idx = figs.indexOf(figClick);
        if (idx > -1) { buildLightbox().openAt(galleryItems(gal), idx); }
      }
    });
  }

  /* shared: does this link OPEN THE QUOTE POPUP rather than navigate? Those
     clicks must never trigger the page loader or nav bar (no navigation). */
  function isEnquiryTrigger(a, href) {
    if (!a) { return false; }
    if (a.closest('[data-dtc-enquiry]')) { return true; }
    href = href || a.getAttribute('href') || '';
    if (href.indexOf('open=quote') !== -1 || href.indexOf('#dtc-enquiry') !== -1) { return true; }
    if (href.indexOf('/pages/contact') !== -1 && /\b(quote|quotes|book|booking|price|pricing|enquir)/i.test(a.textContent || '')) { return true; }
    return false;
  }

  /* ---- branded logo loader: shown on EVERY page change so a slow page
     is never a bare black screen. Fades out as soon as the page is ready
     and re-shows the moment an internal link is clicked. A CSS failsafe
     (dtc-transitions.css) guarantees it can never get stuck even if this
     script fails to load. ---- */
  /* ---- One shared listener for "user clicked a link that will navigate away".
     The preloader and the progress bar each registered their own capturing click
     listener with byte-identical qualifying logic, so every click on the site ran
     closest('a[href]') and constructed a URL object twice. Subscribers are called
     only once a click has qualified. ---- */
  var navClickSubs = [];
  function onInternalNavClick(fn) {
    navClickSubs.push(fn);
    if (onInternalNavClick.bound) { return; }
    onInternalNavClick.bound = true;
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { return; }
      var a = e.target.closest('a[href]');
      if (!a) { return; }
      if (a.target && a.target !== '_self') { return; }
      if (a.hasAttribute('download') || a.getAttribute('data-dtc-enquiry') !== null) { return; }
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) === '#' || /^(mailto:|tel:|javascript:)/i.test(href)) { return; }
      if (isEnquiryTrigger(a, href)) { return; } /* opens the quote popup - no navigation */
      var url;
      try { url = new URL(a.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) { return; }
      if (url.pathname === window.location.pathname && url.hash) { return; } /* same-page anchor */
      /* Same PATH, different query = pagination, facet filters, sorting.
         These must feel instant: the slim progress bar still runs, but the
         full-screen branded loader must not blank the page the shopper is
         standing on. Each subscriber declares whether it cares. */
      var samePath = url.pathname === window.location.pathname;
      /* DEFERRED, because this listener CAPTURES. At capture time nothing has
         had a chance to preventDefault yet, so the test at the top of this
         function always saw `false` and every subscriber started for clicks
         that never navigate - the header basket opens the drawer, the
         collection pager swaps the section, and the progress bar was left
         across the top of the page for the rest of the visit. A task is
         enough: it runs after the event has finished dispatching. */
      window.setTimeout(function () {
        if (e.defaultPrevented) { return; }
        for (var i = 0; i < navClickSubs.length; i++) {
          try { navClickSubs[i](samePath); } catch (err) {}
        }
      }, 0);
    }, true);
  }

  /* ---- navigation progress: a slim yellow bar on internal page
     changes so there is always branded feedback and never a bare
     white wait. Pairs with the page cross-fade (dtc-transitions.css)
     and the white page ground (dtc-chrome.css). ---- */
  function initNavProgress() {
    if (document.body.dataset.dtcNavInit) { return; }
    document.body.dataset.dtcNavInit = '1';
    var bar = document.createElement('div');
    bar.className = 'dtc-navbar';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    var running = false;
    var giveUp = null;
    function clear() {
      window.clearTimeout(giveUp);
      running = false;
      bar.classList.remove('is-active');
      bar.style.transform = '';
    }
    function start() {
      if (running) { return; }
      running = true;
      bar.classList.remove('is-done');
      bar.classList.add('is-active');
      requestAnimationFrame(function () { bar.style.transform = 'scaleX(0.9)'; });
      /* Belt and braces. A navigation can also be abandoned in ways no click
         handler reports - a beforeunload prompt, a download, a redirect that
         never resolves - and a progress bar that is still running is worse
         than one that gave up. If this document is still here, nothing went
         anywhere. */
      window.clearTimeout(giveUp);
      giveUp = window.setTimeout(clear, 6000);
    }
    onInternalNavClick(start);
    /* if a navigation is restored from bfcache, clear the bar */
    window.addEventListener('pageshow', clear);
  }

  /* ---- any link that leaves our site opens in a new tab ---- */
  function initExternalLinks() {
    if (document.body.dataset.dtcExtInit) { return; }
    document.body.dataset.dtcExtInit = '1';
    var host = window.location.hostname;
    document.querySelectorAll('a[href]').forEach(function (a) {
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) === '#' || href.charAt(0) === '/') { return; }
      if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) { return; }
      var url;
      try { url = new URL(a.href); } catch (e) { return; }
      if (url.hostname && url.hostname !== host) {
        a.target = '_blank';
        var rel = (a.getAttribute('rel') || '').split(/\s+/);
        if (rel.indexOf('noopener') === -1) { rel.push('noopener'); }
        if (rel.indexOf('noreferrer') === -1) { rel.push('noreferrer'); }
        a.setAttribute('rel', rel.join(' ').trim());
      }
    });
  }

  /* ---- whole-card click: make any .dtc-card that is not itself a link but
     has a single primary CTA (its foot button/link) clickable anywhere on the
     card, so pressing the image/title/body opens the same place as the button.
     Real controls inside keep working, text selection is preserved, and
     keyboard users still tab to the real button - this is a mouse enhancement. ---- */
  function initClickableCards() {
    if (document.body.dataset.dtcCardClick) { return; }
    document.body.dataset.dtcCardClick = '1';
    function cta(card) {
      return card.querySelector('.dtc-card__foot a[href], .dtc-card__foot button, .dtc-card__footer a[href], .dtc-card__footer button');
    }
    document.querySelectorAll('.dtc-card').forEach(function (card) {
      if (card.tagName === 'A' || card.closest('a')) { return; }
      if (cta(card)) { card.classList.add('dtc-clickable'); }
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest('a[href], button, input, textarea, select, label, summary, [data-dtc-enquiry]')) { return; }
      var card = e.target.closest('.dtc-clickable');
      if (!card) { return; }
      if (window.getSelection && String(window.getSelection()).length) { return; }
      var c = cta(card);
      if (c) { c.click(); }
    });
  }

  function rvEscape(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function rvProductURL(value) {
    try {
      var u = new URL(String(value || ''), location.origin);
      if (u.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(u.pathname)) { return ''; }
      return u.pathname + u.search + u.hash;
    } catch (e) { return ''; }
  }

  function rvCardHTML(p) {
    var url = rvProductURL(p && p.url);
    if (!url) { return ''; }
    /* a name saved before the naming rule, printed by it (snippets/tdhq-name-js) */
    if (window.tdhqName) { p.title = window.tdhqName(p.title); }
    /* the fallback card asks for the card's own sizes, as tdhq-product-card does (TdhqImg) */
    var media = p.image
      ? '<img src="' + rvEscape(window.TdhqImg.sized(p.image, 540)) + '" srcset="' +
        rvEscape([180, 260, 360, 540].map(function (w) { return window.TdhqImg.sized(p.image, w) + ' ' + w + 'w'; }).join(', ')) +
        '" sizes="(min-width: 1400px) 22vw, (min-width: 1000px) 24vw, 45vw" width="540" height="540" loading="lazy" alt="' + rvEscape(p.title) + '" data-tdhq-fit="card">'
      : '';
    var priceHtml;
    if (p.hasPrice === 0) {
      priceHtml = '<p class="dtc-shop__price"><span>Price on request</span></p>';
    } else if (p.price) {
      priceHtml = '<p class="dtc-shop__price">' + (p.priceVaries ? '<span class="dtc-shop__from">From</span> ' : '') + '<span>' + rvEscape(p.price) + '</span></p>';
    } else {
      priceHtml = '<p class="dtc-shop__price"><span>Price on request</span></p>';
    }
    var top = p.eyebrow || p.vendor;
    var eyebrow = top ? '<p class="dtc-shop__vendor"><span>' + rvEscape(top) + '</span></p>' : '';
    /* Was: a `duration` line (a service length — null on every retail product)
       plus an UNCONDITIONAL "Free, no-obligation quote" line. That second line
       was not gated by anything, so every recently-viewed card on a retail store
       promised a quote. The Liquid cards had the same text removed earlier; this
       JS copy of the card was missed. Now: no meta list at all. p.stock is the
       copy saved in localStorage when the product was viewed, possibly days ago,
       so 'Only N left' or 'Out of stock' could be out of date. Stock is only
       stated on server-rendered cards. */
    var meta = '';
    return '<a class="dtc-card dtc-shop__card dtc-reveal is-in" href="' + rvEscape(url) + '">' +
      '<div class="dtc-card__media">' + media + '</div>' +
      '<div class="dtc-card__body">' + eyebrow +
      '<h3 class="dtc-shop__title"><span>' + rvEscape(p.title) + '</span></h3>' + meta + priceHtml +
      '<span class="dtc-btn dtc-btn--outline dtc-shop__btn">View</span>' +
      '</div></a>';
  }

  // Recently viewed services: render history from localStorage into the
  // (hidden) section, then record the current product for next time.
  function initRecentlyViewed() {
    var KEY = 'dtc_recent_v1';
    var list = [];
    try { list = JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { list = []; }
    if (!Array.isArray(list)) { list = []; }

    var current = null;
    var cur = document.querySelector('[data-dtc-current-product]');
    if (cur) { try { current = JSON.parse(cur.textContent); } catch (e) { current = null; } }

    var wrap = document.querySelector('[data-dtc-recent]');
    if (wrap && !wrap.dataset.dtcInit) {
      wrap.dataset.dtcInit = '1';
      var grid = wrap.querySelector('[data-dtc-recent-grid]');
      var limit = parseInt(wrap.getAttribute('data-dtc-recent-limit'), 10) || 4;
      var show = list.filter(function (p) {
        return p && rvProductURL(p.url) && (!current || String(p.id) !== String(current.id));
      }).slice(0, limit);
      if (grid && show.length) {
        wrap.removeAttribute('hidden');
        /* ONE CARD COMPONENT SITE-WIDE (FIX-LIST 3c).

           This built its own card markup in JavaScript, which made it the
           fourth place a product card was defined. It now asks the SERVER for
           the same card every other row uses, via the ?view=card template — so
           the wishlist heart, the quick-add form, the stock line and the
           alignment reservations arrive for free and cannot drift.

           rvCardHTML stays as the fallback for any handle that fails to fetch:
           a recently-viewed row that renders nothing would look broken, and the
           locally stored data is enough to draw a reasonable card. */
        Promise.all(show.map(function (p) {
          var handle = String(p.url || '').split('/products/')[1];
          if (!handle) { return Promise.resolve(null); }
          handle = handle.split('?')[0].split('#')[0];
          /* the stored URL is already percent-encoded: decode, then encode
             once, or "dtlr%C2%AE" becomes "dtlr%25C2%25AE" and 404s */
          try { handle = decodeURIComponent(handle); } catch (e) { /* keep as stored */ }
          return fetch('/products/' + encodeURIComponent(handle) + '?view=card', { headers: { 'Accept': 'text/html' } })
            .then(function (r) { return r.ok ? r.text() : (r.status === 404 ? 'GONE' : null); })
            .catch(function () { return null; });
        })).then(function (cards) {
          /* a product that no longer exists is dropped, not drawn */
          var keep = [];
          cards.forEach(function (c, i) { if (c !== 'GONE') { keep.push(c || rvCardHTML(show[i])); } });
          var html = keep;
          if (!html.length) { wrap.setAttribute('hidden', ''); return; }
          /* SOLD OUT LAST, like every other rail. The card the server sends
             carries tdhq-pcard--out when the product is unavailable, so the
             order is decided on the same fact the card shows. A stable sort:
             within each group the visit order is kept (newest first). */
          var inStock = html.filter(function (c) { return c.indexOf('tdhq-pcard--out') === -1; });
          var soldOut = html.filter(function (c) { return c.indexOf('tdhq-pcard--out') !== -1; });
          grid.innerHTML = inStock.concat(soldOut).join('');
          /* The carousel wrapping this grid computes arrow state only on
             scroll/resize — async insertion triggers neither, so the arrows
             would stay in their empty-track state (hidden). One coalesced
             resize recomputes them. */
          window.dispatchEvent(new Event('resize'));
        });
      }
    }

    if (current && current.id) {
      list = list.filter(function (p) { return p && String(p.id) !== String(current.id); });
      list.unshift(current);
      list = list.slice(0, 8);
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }
  }

  function initBeforeAfter() {
    if (!document.body.dataset.dtcBaDrag) {
      document.body.dataset.dtcBaDrag = '1';
      document.addEventListener('input', function (e) {
        var range = e.target.closest('[data-dtc-ba-range]');
        if (!range) { return; }
        var ba = range.closest('[data-dtc-ba]');
        if (ba) { ba.dataset.dtcBaUser = '1'; ba.style.setProperty('--pos', range.value + '%'); }
      });
      /* grabbing the handle stops the intro animation instantly (before any input fires) */
      document.addEventListener('pointerdown', function (e) {
        var range = e.target.closest('[data-dtc-ba-range]');
        if (range) { var ba = range.closest('[data-dtc-ba]'); if (ba) { ba.dataset.dtcBaUser = '1'; } }
      });
    }
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('[data-dtc-ba]:not([data-dtc-ba-ready])').forEach(function (ba) {
      ba.setAttribute('data-dtc-ba-ready', '1');
      var range = ba.querySelector('[data-dtc-ba-range]');
      if (reduce) { ba.style.setProperty('--pos', '50%'); return; }
      function play() {
        var start = null;
        var dur = 2200;
        function frame(now) {
          if (ba.dataset.dtcBaUser) { return; }
          if (start === null) { start = now; }
          var p = (now - start) / dur;
          if (p >= 1) { ba.style.setProperty('--pos', '50%'); if (range) { range.value = 50; } return; }
          var env = Math.sin(p * Math.PI);
          var pos = 50 + 34 * env * Math.sin(p * Math.PI * 3);
          ba.style.setProperty('--pos', pos.toFixed(2) + '%');
          if (range) { range.value = pos; }
          requestAnimationFrame(frame);
        }
        requestAnimationFrame(frame);
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) { io.disconnect(); play(); } });
        }, { threshold: 0.4 });
        io.observe(ba);
      } else { play(); }
    });
  }

  /* ---- blog article formatter: old posts were saved as one sentence per
     paragraph with fake <p><strong> headings. Turn those into real <h2>
     headings and merge the choppy sentences into readable paragraphs so
     every post reads like the DTC design, without editing the content. ---- */
  /* Shopify renders /policies/* with its own fixed markup and ignores
     templates/policy.liquid entirely, so the only place the policy BODY can be
     corrected is here. The Refund policy's stored body opens with its own <h1>
     ("Refund and Returns Policy") on top of the page heading Shopify already
     prints, giving that page two competing top-level headings.

     Demoted rather than removed: the body heading is real content and often
     phrased differently from the page title, so deleting it would lose wording
     the shop wrote. As an <h2> it becomes the first section of the document
     instead of a rival title. Editing the stored policy text would be the other
     fix, but that is an admin content change, not a theme one.

     BODY ONLY (2026-09-23). This used to query '.shopify-policy__body,
     .shopify-policy__container'. querySelector returns the first match in
     document order, which is the CONTAINER (it wraps the body), so the loop
     also demoted Shopify's own page title: every policy page measured 0 h1
     and its title lost the heading style that targets an h1.

     The body's OPENING h1 is the page title typed again ("Refund and Returns
     Policy" under "Refund policy"). dtc-chrome.css hides it before this script
     runs; it is demoted like the rest and marked so the same rule keeps it
     hidden afterwards, which leaves one h1 and nothing on screen moves. */
  function initPolicyHeadings() {
    var body = document.querySelector('.shopify-policy__body');
    if (!body || body.dataset.tdhqPolicyFmt) { return; }
    body.dataset.tdhqPolicyFmt = '1';
    Array.prototype.slice.call(body.querySelectorAll('h1')).forEach(function (el) {
      var dup = el.matches('.rte > h1:first-child');
      var h2 = document.createElement('h2');
      if (dup) { h2.setAttribute('data-tdhq-policy-dup', ''); }
      /* Copy child NODES rather than innerHTML: same result for markup, but it
         moves the existing nodes instead of re-parsing a string. */
      while (el.firstChild) { h2.appendChild(el.firstChild); }
      for (var i = 0; i < el.attributes.length; i++) {
        h2.setAttribute(el.attributes[i].name, el.attributes[i].value);
      }
      el.parentNode.replaceChild(h2, el);
    });
  }

  function initArticleFormat() {
    var body = document.querySelector('.dtc-article__body');
    if (!body || body.dataset.dtcFmt) { return; }
    body.dataset.dtcFmt = '1';
    var h1 = document.querySelector('.dtc-article__title');
    var titleText = h1 ? h1.textContent.trim().toLowerCase() : '';

    function looksLikeTitle(t) {
      t = t.trim().toLowerCase();
      if (!titleText || t.length < 10) { return false; }
      return t === titleText || titleText.indexOf(t.slice(0, 22)) !== -1 || t.indexOf(titleText.slice(0, 22)) !== -1;
    }

    /* 0. Strip pasted-in content artifacts before restructuring: private meta
          lines (SEO tags / keywords), leftover emoji and en/em dashes, so posts
          read cleanly and consistently without editing the stored content. */
    (function cleanArtifacts() {
      var EMOJI = /([\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}][️‍]?)\s*/gu;
      Array.prototype.slice.call(body.querySelectorAll('p, div')).forEach(function (p) {
        var t = p.textContent.trim();
        if (t.length < 400 && /^(seo tags?|seo|keywords?|tags?|meta(\s+description)?)\s*[:\-–—]/i.test(t)) {
          p.remove();
        }
      });
      var walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, null);
      var tn, list = [];
      while ((tn = walker.nextNode())) { list.push(tn); }
      list.forEach(function (node) {
        var v = node.nodeValue;
        if (!v) { return; }
        var out = v.replace(EMOJI, '').replace(/\s*[–—]\s*/g, ', ').replace(/\s{2,}/g, ' ');
        if (out !== v) { node.nodeValue = out; }
      });
    })();

    /* 1. One <h1> per page: the template already renders the post title. Drop a
          body <h1> that repeats it; otherwise demote to <h2> for a valid outline. */
    Array.prototype.slice.call(body.querySelectorAll('h1')).forEach(function (el) {
      if (looksLikeTitle(el.textContent)) { el.remove(); return; }
      var h2 = document.createElement('h2');
      h2.innerHTML = el.innerHTML;
      el.parentNode.replaceChild(h2, el);
    });

    /* The section template now demotes body <h1> to <h2> in Liquid, so the loop
       above usually finds nothing — the raw HTML must be correct for crawlers
       that never run this script. That means the "drop a heading that just
       repeats the post title" rule has to look at <h2> as well, or a duplicated
       title would survive purely because it arrived already demoted. */
    Array.prototype.slice.call(body.querySelectorAll('h2')).forEach(function (el) {
      if (looksLikeTitle(el.textContent)) { el.remove(); }
    });

    /* Authored content carries empty headings (<h1></h1> used as spacing).
       Demoted they become empty <h2>s: invisible on the page, but real, blank
       entries in the document outline that a screen reader announces. */
    Array.prototype.slice.call(body.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach(function (el) {
      if (!el.textContent.trim() && !el.querySelector('img')) { el.remove(); }
    });

    /* 2. Fake bold headings -> real <h2> (whole paragraph is a short <strong>). */
    Array.prototype.slice.call(body.querySelectorAll('p')).forEach(function (p) {
      if (p.children.length !== 1) { return; }
      var c = p.children[0];
      if (c.tagName !== 'STRONG' && c.tagName !== 'B') { return; }
      var txt = p.textContent.trim();
      if (txt.length < 3 || txt.length > 90) { return; }
      if (p.textContent.trim() !== c.textContent.trim()) { return; }
      if (looksLikeTitle(txt)) { p.remove(); return; }
      var h = document.createElement('h2');
      h.textContent = txt;
      p.parentNode.replaceChild(h, p);
    });

    /* 3. Sentence-per-line <br> blocks -> individual paragraphs so the merge step
          can regroup them into clean, readable paragraphs. */
    Array.prototype.slice.call(body.querySelectorAll('p')).forEach(function (p) {
      if (p.querySelectorAll('br').length < 2) { return; }
      var parts = p.innerHTML.split(/<br\s*\/?>/i).map(function (s) { return s.trim(); }).filter(Boolean);
      if (parts.length < 2) { return; }
      var frag = document.createDocumentFragment();
      parts.forEach(function (seg) {
        var np = document.createElement('p');
        np.innerHTML = seg;
        frag.appendChild(np);
      });
      p.parentNode.replaceChild(frag, p);
    });

    /* 4. Merge consecutive short paragraphs into ~2-3 sentence groups. */
    var child = body.firstElementChild;
    while (child) {
      var next = child.nextElementSibling;
      if (child.tagName === 'P') {
        while (next && next.tagName === 'P' && (child.textContent.length + next.textContent.length) < 300) {
          child.innerHTML = child.innerHTML.trim() + ' ' + next.innerHTML.trim();
          var toRemove = next;
          next = next.nextElementSibling;
          toRemove.remove();
        }
      }
      child = next;
    }

    /* 5. Lead-in styling on the opening paragraph. */
    var firstP = body.querySelector('p');
    if (firstP && firstP.textContent.trim().length > 40) { firstP.classList.add('dtc-article__lead'); }

    /* 6. One topic-relevant photo mid-article when the post has none of its own.
       Images come from the "Fallback article images" theme setting (keyword|url), never
       from hardcoded URLs — a hardcoded one would put the original store's photos on
       every other store using this theme. Not configured => insert nothing. */
    var artImgs = window.__dtcArtImgs;
    if (artImgs && artImgs.length &&
        !body.querySelector('.dtc-article__inlineimg') && body.querySelectorAll('img').length === 0) {
      var pick = null;
      for (var ai = 0; ai < artImgs.length; ai++) {
        var kw = String(artImgs[ai].k || '').toLowerCase();
        if (kw && titleText.toLowerCase().indexOf(kw) !== -1) { pick = artImgs[ai]; break; }
      }
      /* last entry is the catch-all when no keyword matches */
      if (!pick) { pick = artImgs[artImgs.length - 1]; }
      var h2s = body.querySelectorAll('h2');
      var anchor = h2s.length >= 2 ? h2s[1] : body.querySelectorAll('p')[3];
      if (anchor && pick && pick.u) {
        var im = document.createElement('img');
        im.sizes = '(min-width: 900px) 760px, 92vw';
        im.srcset = [360, 540, 720, 900, 1200].map(function (w) { return window.TdhqImg.sized(pick.u, w) + ' ' + w + 'w'; }).join(', ');
        im.src = window.TdhqImg.sized(pick.u, 1200);
        im.alt = '';
        im.className = 'dtc-article__inlineimg';
        im.loading = 'lazy';
        im.decoding = 'async';
        anchor.parentNode.insertBefore(im, anchor);
      }
    }
  }

  /* Blog card excerpts/titles are auto-generated from post content, so they
     inherit the same emoji/dash artifacts as the bodies. Clean them site-wide
     (homepage feed, blog listing, "more from the journal") for consistency. */
  function initBlogCards() {
    var els = document.querySelectorAll('.dtc-blog__excerpt, .dtc-blog__title');
    if (!els.length) { return; }
    var EMOJI = /([\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}][️‍]?)\s*/gu;
    Array.prototype.slice.call(els).forEach(function (el) {
      if (el.dataset.dtcClean) { return; }
      el.dataset.dtcClean = '1';
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var tn, list = [];
      while ((tn = walker.nextNode())) { list.push(tn); }
      list.forEach(function (node) {
        var v = node.nodeValue;
        if (!v) { return; }
        var out = v.replace(EMOJI, '').replace(/\s*[–—]\s*/g, ', ').replace(/\s{2,}/g, ' ');
        if (out !== v) { node.nodeValue = out; }
      });
    });
  }

  /* ------------------------------------------------------------------
     DEAD IN-PAGE LINKS

     Several sections correctly hide themselves when they have no data - an
     empty collection, a brand list with no entries. What they cannot do is
     tell the OTHER sections on the page, so a hero button and two route cards
     on New & Offers went on pointing at `#bundles-and-kits` and `#new-brands`
     for sections that were not rendered. Clicking them did nothing at all:
     no scroll, no message, no clue that anything was wrong.

     The theme already refuses to print a menu link to a page that does not
     exist (snippets/tdhq-link-alive.liquid). This is the same rule for
     fragments, applied where only the browser can apply it - after the
     document exists and it is knowable whether the target is in it.

     Scoped to our own sections, because a parent-theme link like
     `#login-modal` targets a node its own script creates later; and skipped in
     the theme editor, where a merchant needs to see the control they are
     configuring even while its destination is empty.
     ------------------------------------------------------------------ */
  function hideDeadAnchors() {
    if (window.Shopify && window.Shopify.designMode) { return; }
    var links = document.querySelectorAll('.dtc a[href^="#"], .tdhq a[href^="#"]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      var frag = href.slice(1);
      if (!frag || frag === '!') { continue; }
      /* A fragment a script answers is not a dead link: the sign-in page's
         "Forgotten your password?" (#recover) and "Back to sign in" (#login)
         switch panels in dtc-account and have no element of that id, so this
         hid the only way to reset a password (audit service-1). */
      if (links[i].matches('[data-dtc-recover-show], [data-dtc-recover-hide]')) { continue; }
      var target = null;
      try { target = document.getElementById(frag) || document.getElementsByName(frag)[0]; } catch (e) { continue; }
      if (target) { continue; }
      /* Hidden, not removed: a merchant reading the page source (or this
         theme's own docs) can still see what was meant to be there, and the
         moment the section has content the link works again with no code
         change. */
      links[i].hidden = true;
      links[i].setAttribute('data-dtc-dead-anchor', frag);

      /* A wrapper that now holds nothing is worse than the dead link was: it
         keeps its padding and its min-height, so the page shows a 44px band of
         nothing where a button used to be. Collapse empty ancestors, but only a
         couple of levels and only while they are genuinely empty - never a
         whole section. */
      var node = links[i].parentElement;
      var climbed = 0;
      while (node && climbed < 3 && !node.matches('section, [id^="shopify-section"], main, body')) {
        var live = false;
        for (var k = 0; k < node.children.length; k++) {
          if (!node.children[k].hidden) { live = true; break; }
        }
        /* textContent includes the text of the link we just hid, so testing it
           always found "content" and the wrapper never collapsed. Count only
           what is still VISIBLE: text nodes, and children that are not hidden. */
        var text = '';
        for (var t = 0; t < node.childNodes.length; t++) {
          var cn = node.childNodes[t];
          if (cn.nodeType === 3) { text += cn.nodeValue; }
        }
        if (live || text.trim()) { break; }
        node.hidden = true;
        node.setAttribute('data-dtc-dead-anchor-wrap', '');
        node = node.parentElement;
        climbed++;
      }
    }

    /* A card grid sizes its columns from the cards the SERVER rendered, and its
       last-row rules count children with nth-child. A card hidden here still
       counted for both: on New & Offers the three-up routes row kept an empty
       third column where the New Brands card had been. Hidden cards move out of
       the grid (still in the page and still hidden), and the per-row class
       shrinks to the cards that are left - the same rule tdhq-card-grid applies
       on the server, which leaves the fixed contact layout alone. */
    var grids = document.querySelectorAll('.tdhq-cards');
    for (var g = 0; g < grids.length; g++) {
      var grid = grids[g];
      if (grid.hidden) { continue; }
      var gone = [];
      for (var c = 0; c < grid.children.length; c++) {
        if (grid.children[c].hidden) { gone.push(grid.children[c]); }
      }
      if (!gone.length) { continue; }
      for (var h = gone.length - 1; h >= 0; h--) {
        grid.parentNode.insertBefore(gone[h], grid.nextSibling);
      }
      if (grid.classList.contains('tdhq-cards--contact')) { continue; }
      var left = grid.children.length;
      var pr = grid.className.match(/\btdhq-grid--pr(\d+)\b/);
      if (pr && left > 0 && left < +pr[1]) {
        grid.classList.remove(pr[0]);
        grid.classList.add('tdhq-grid--pr' + left);
        grid.style.setProperty('--cards-per-row', left);
      }
    }

    /* A quick-links band left with one link is a full-width band holding a
       single pill, usually repeating a button right beside it (new-and-offers:
       its collection links are skipped until those collections exist). Fewer
       than two live links hides the whole section; it comes back on its own
       once more links render. */
    var bands = document.querySelectorAll('.tdhq-quicklinks');
    for (var q = 0; q < bands.length; q++) {
      if (bands[q].querySelectorAll('.tdhq-quicklinks__list > li:not([hidden])').length < 2) {
        (bands[q].closest('.shopify-section') || bands[q]).hidden = true;
      }
    }
  }

  /* BAND JOINS. Every page is a stack of full-width bands that alternate
     light and grey, so each one reads as its own section. WHICH bands render
     depends on the product's own content, so two bands of the same ground end
     up side by side - story then related on every product without curated
     content, reviews then trust wherever the brand band is empty. The join
     then disappears: 132 to 222px of one unbroken ground with nothing to say
     where one section ended (measured on 7 product pages, 2026-09-22).

     Which grounds actually meet is only known once the page is rendered, so it
     is read here and marked with a hairline the width of the content. The line
     is positioned, not in the flow, so nothing moves. */
  function initBandJoins() {
    var main = document.getElementById('MainContent');
    if (!main) { return; }
    /* The colour the eye sees at an edge: the first painted element down the
       chain of rendered first (or last) children. */
    function edgeColour(el, fromBottom) {
      var node = el, depth = 0;
      while (node && depth < 14) {
        var c = window.getComputedStyle(node).backgroundColor;
        if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') { return c; }
        var kids = [].filter.call(node.children, function (k) { return k.getBoundingClientRect().height > 0; });
        node = fromBottom ? kids[kids.length - 1] : kids[0];
        depth++;
      }
      return '';
    }
    /* Padding between a band's edge and its content: summed down the chain
       of rendered first (or last) children, three levels deep (a scheme
       wrapper, a container, a section box), stopping at anything with a ground
       of its own below the wrapper - a card's inner padding is content. */
    function edgePad(el, fromBottom) {
      var sum = 0, node = el, depth = 0;
      while (node && depth < 3) {
        var kids = [].filter.call(node.children, function (k) { return k.getBoundingClientRect().height > 0; });
        node = fromBottom ? kids[kids.length - 1] : kids[0];
        if (!node) { break; }
        var cs = window.getComputedStyle(node);
        if (depth > 0 && cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent') { break; }
        sum += parseFloat(fromBottom ? cs.paddingBottom : cs.paddingTop) || 0;
        depth++;
      }
      return sum;
    }
    /* The join between a band and the one above it, or null. Hidden bands
       (height 0) are skipped by the caller; thin chrome strips (breadcrumbs,
       notices) are not bands: a line under the breadcrumb on every collection
       page is noise, not a boundary, so nothing joins across one. */
    function joinFor(sec, prev) {
      if (!prev || sec.getBoundingClientRect().height < 120 || prev.getBoundingClientRect().height < 120) { return null; }
      var above = edgeColour(prev, true);
      var below = edgeColour(sec, false);
      if (!above || above !== below) { return null; }
      var rgb = below.match(/\d+/g);
      return {
            sec: sec,
          /* The line sits in the middle of the gap between the two bands.
             Drawn at the section's top edge it went through the heading below
             (contact "Send us a message", rewards "How HQ Rewards works": the
             rule 2-5px INSIDE the cap height, 2026-09-23), because "same
             ground, one gap" (tdhq-core.css) removes that section's top
             padding, so its top edge IS its heading. The padding that does
             exist is read off each band (first rendered child chain below the
             line, last one above it), so the offset comes out as 0 when both
             bands keep their padding. */
            top: ((edgePad(sec, false) - edgePad(prev, true)) / 2) + 'px',
            dark: !!(rgb && (Number(rgb[0]) + Number(rgb[1]) + Number(rgb[2])) / 3 < 128)
      };
    }
    /* Every band is read before any is marked: a mark between two reads
       would make the next read lay the page out again. */
    function mark(joins) {
      joins.forEach(function (j) {
        j.sec.classList.add('dtc-band-join');
        j.sec.style.setProperty('--join-top', j.top);
        if (j.dark) { j.sec.classList.add('dtc-band-join--dark'); }
      });
    }
    var secs = [].filter.call(main.children, function (s) { return s.classList.contains('shopify-section'); });
    /* The band above: the nearest earlier section that renders (a thin strip
       counts, so nothing joins across it). */
    function prevOf(i) {
      for (var p = i - 1; p >= 0; p--) { if (secs[p].getBoundingClientRect().height) { return p; } }
      return -1;
    }
    /* U.52 (9 Oct 2026): bands the page is still skipping (content-visibility:
       auto below the fold, tdhq-fix-home.css + tdhq-fix-global.css) are read
       once BOTH bands of a join are near the screen. Read at load they were
       laid out just for this: a forced layout of the whole page, ~60 ms on a
       phone home page. Every other join is read at load, exactly as before.
       The mark is a positioned hairline, so marking later moves nothing. */
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var skipped = secs.map(function (s) {
      if (window.getComputedStyle(s).contentVisibility !== 'auto') { return false; }
      var r = s.getBoundingClientRect();
      return r.top > vh * 1.5 || r.bottom < -vh * 0.5;
    });
    var useIo = 'IntersectionObserver' in window;
    var near = [], done = [], left = secs.length, now = [];
    for (var n = 0; n < secs.length; n++) {
      var pn = prevOf(n);
      if (useIo && (skipped[n] || (pn >= 0 && skipped[pn]))) { continue; }
      done[n] = true;
      left--;
      var jn = pn < 0 ? null : joinFor(secs[n], secs[pn]);
      if (jn) { now.push(jn); }
    }
    mark(now);
    if (left <= 0) { return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { near[secs.indexOf(e.target)] = true; } });
      var found = [];
      for (var i = 0; i < secs.length; i++) {
        if (done[i] || !near[i]) { continue; }
        var p = prevOf(i);
        if (p >= 0 && !near[p]) { continue; }
        done[i] = true;
        left--;
        var j = p < 0 ? null : joinFor(secs[i], secs[p]);
        if (j) { found.push(j); }
      }
      mark(found);
      if (left <= 0) { io.disconnect(); }
    }, { rootMargin: '25% 0px' });
    secs.forEach(function (s) { io.observe(s); });
  }

  /* ---- COUNT-UP. A statistic counts up from 0 to its figure the first time
     it scrolls into view (client, About page, June: "make animation to
     statistics, smooth animation that animated the numbers changing"; the
     rebuild had lost it). Marked with data-dtc-count by the trust bar ("7" in
     "7 Days") and the notice stat (the Rewards "12" months). Whole numbers
     only - "24/7" or "4.9" are left as written. The page is served with the
     real figure, so it reads right with no script, to search engines and to
     anyone who asks for reduced motion (nothing moves for them). Duration and
     curve are the theme's "content arriving" motion: --tdhq-t-slow and the
     shared ease, cubic-bezier(0.4, 0, 0.2, 1). ---- */
  function initCountUp() {
    var els = document.querySelectorAll('[data-dtc-count]:not([data-dtc-counted])');
    if (!els.length) { return; }
    if (reduce || !('IntersectionObserver' in window)) { return; }
    if (window.Shopify && window.Shopify.designMode) { return; }
    /* the token as milliseconds (the built CSS stores .42s, which parseFloat read as 0.42 ms) */
    var dur = window.TdhqMotion ? window.TdhqMotion.ms('--tdhq-t-slow', 420) : 420;
    function ease(x) {
      /* cubic-bezier(0.4, 0, 0.2, 1): x(t) = 1.6t^3 - 1.8t^2 + 1.2t and
         y(t) = -2t^3 + 3t^2. Solve x(t) = x by Newton's method, return y(t). */
      if (x <= 0 || x >= 1) { return x; }
      var t = x;
      for (var i = 0; i < 8; i++) {
        var fx = ((1.6 * t - 1.8) * t + 1.2) * t - x;
        var dx = (4.8 * t - 3.6) * t + 1.2;
        if (!dx) { break; }
        t -= fx / dx;
      }
      return (-2 * t + 3) * t * t;
    }
    function run(el) {
      var to = parseInt(el.getAttribute('data-dtc-count-to'), 10);
      var fin = el.getAttribute('data-dtc-count-final');
      var t0 = null;
      function frame(now) {
        if (reduce) { el.textContent = fin; return; }
        if (t0 === null) { t0 = now; }
        var p = Math.min(1, (now - t0) / dur);
        el.textContent = p < 1 ? String(Math.round(to * ease(p))) : fin;
        if (p < 1) { requestAnimationFrame(frame); }
      }
      el.textContent = '0';
      requestAnimationFrame(frame);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) { return; }
        io.unobserve(e.target);
        run(e.target);
      });
    }, { threshold: 0.6 });
    Array.prototype.forEach.call(els, function (el) {
      var txt = el.textContent.trim();
      if (!/^\d+$/.test(txt) || !parseInt(txt, 10)) { return; }
      el.setAttribute('data-dtc-counted', '');
      el.setAttribute('data-dtc-count-to', txt);
      el.setAttribute('data-dtc-count-final', txt);
      /* one box the width of the final figure, so the words beside it never
         move while it counts (tabular digits, set in the stylesheet) */
      el.style.display = 'inline-block';
      el.style.minWidth = txt.length + 'ch';
      el.style.textAlign = 'end';
      /* Below the fold it waits at 0, so the figure is never seen whole and
         then reset as it arrives. One already on screen counts from where it
         is shown. */
      var r = el.getBoundingClientRect();
      if (r.top > window.innerHeight || r.bottom < 0) { el.textContent = '0'; }
      io.observe(el);
    });
  }

  /* ---- CONTENT-VISIBILITY RELEASE (15 Oct 2026). Sections below the fold
     are served with content-visibility: auto (U.52, tdhq-fix-home.css and
     tdhq-fix-global.css) so the first paint skips them. The same rule keeps
     each one painted on its own once it is on screen, and two bands meeting
     on a fractional pixel then snap apart on a scaled display: a black line
     of page ground between them (owner, homepage, Lifestyle & Clothing Brands
     over HQ Rewards). A section the browser has started rendering gets
     .is-cv-on, which hands it back to normal painting for good. Sections
     rendered before this script ran are read once after the next frame: the
     section box itself always reads visible, so its content is asked. ---- */
  function initCvRelease() {
    var sel = '#MainContent > .shopify-section, .tdhq-footer-section';
    function on(el) { if (el && el.classList && !el.classList.contains('is-cv-on') && el.matches(sel)) { el.classList.add('is-cv-on'); } }
    document.addEventListener('contentvisibilityautostatechange', function (e) {
      if (!e.skipped) { on(e.target); }
    }, true);
    if (!document.body.checkVisibility) { return; }
    window.requestAnimationFrame(function () {
      var skipped = [];
      [].forEach.call(document.querySelectorAll(sel), function (el) {
        if (el.classList.contains('is-cv-on')) { return; }
        if (window.getComputedStyle(el).contentVisibility !== 'auto') { return; }
        var c = el.querySelector(':scope > :not(style, script, link, template)');
        if (c && c.checkVisibility({ contentVisibilityAuto: true })) { on(el); } else { skipped.push(el); }
      });
      if (skipped.length) { renderAhead(skipped); }
    });

    /* RENDERED AHEAD OF THE SHOPPER (owner 20 Oct 2026: phone scrolling
       "stutters and clips"). A skipped band did all its style and layout in the
       frame it scrolled near: 100-330ms on the home page at phone speed (4x CPU
       trace), so the scroll stalled and the band showed blank for a moment.
       From the shopper's first touch or scroll, or 3s after the page has
       loaded if that comes first, every skipped band is released in idle
       time, the one nearest the view first, one per pause, never while the
       page is moving (a four-screen lookahead still left the band after it to
       a fast first swipe: 282ms). Nothing runs before the page has loaded (a
       touch during loading waits for it), so the first paint keeps the saving. */
    function renderAhead(list) {
      var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 100); };
      var queue = list.slice(), waiting = false, lastScroll = 0, started = false;
      var starts = ['scroll', 'touchstart', 'pointerdown', 'keydown'];
      function later() { if (queue.length && !waiting) { waiting = true; idle(pump, { timeout: 2000 }); } }
      function nearest() {
        var best = -1, bestD = Infinity, vh = window.innerHeight;
        for (var i = 0; i < queue.length; i++) {
          var r = queue[i].getBoundingClientRect();
          var d = r.top > vh ? r.top - vh : (r.bottom < 0 ? -r.bottom : 0);
          if (d < bestD) { bestD = d; best = i; }
        }
        return best < 0 ? null : queue.splice(best, 1)[0];
      }
      function pump() {
        waiting = false;
        if (Date.now() - lastScroll < 160) { later(); return; }
        var el = nearest();
        while (el && el.classList.contains('is-cv-on')) { el = nearest(); }
        if (!el) { return; }
        on(el);
        void el.offsetHeight;   /* its style and layout now, in idle time, not in a scrolling frame */
        later();
      }
      function start() {
        if (started) { return; }
        if (document.readyState !== 'complete') { window.addEventListener('load', start); return; }
        started = true;
        starts.forEach(function (t) { window.removeEventListener(t, start, true); });
        window.addEventListener('scroll', function () { lastScroll = Date.now(); }, { passive: true });
        lastScroll = Date.now();
        later();
      }
      starts.forEach(function (t) { window.addEventListener(t, start, { capture: true, passive: true }); });
      function afterLoad() { setTimeout(start, 3000); }
      if (document.readyState === 'complete') { afterLoad(); } else { window.addEventListener('load', afterLoad); }
    }
  }

  /* Run as steps (window.TdhqBoot, 21 Oct): the same order, one step per carousel / slider / accordion, with the
     browser given a turn about every 12 ms so a swipe that starts while the page sets up is not held. `then` runs
     after the last step. */
  function init(then) {
    var steps = [hideDeadAnchors, initBandJoins, initCvRelease, initArticleFormat, initPolicyHeadings, initBlogCards,
      initNavProgress, initExternalLinks, initClickableCards, initRecentlyViewed];
    document.querySelectorAll('[data-dtc-carousel]').forEach(function (el) { steps.push(function () { initCarousel(el); }); });
    steps.push(initDragRails, initLogoTrim);
    document.querySelectorAll('[data-dtc-hero-slider]').forEach(function (el) { steps.push(function () { initHeroSlider(el); }); });
    document.querySelectorAll('[data-dtc-acc]').forEach(function (el) { steps.push(function () { initAccordion(el); }); });
    steps.push(initVideoSound, initVideoPlayback, initGalleries, initServiceMedia, initBeforeAfter, initInstaFeed,
      initBackToTop, initReveal, initCountUp, function () { initDisclosures(); }, initVideoImgAlt);
    if (typeof then === 'function') { steps.push(then); }
    if (window.TdhqBoot) { window.TdhqBoot.steps(steps); } else { steps.forEach(function (fn) { fn(); }); }
  }

  /* a11y: Shopify's decorative video-preview images ship without an alt attribute and are
     injected when the lazy videos load, so watch for them and mark them decorative. */
  function initVideoImgAlt() {
    function fix() {
      var imgs = document.querySelectorAll('video img:not([alt])');
      for (var i = 0; i < imgs.length; i++) { imgs[i].setAttribute('alt', ''); }
    }
    fix();
    /* The preview img is only ever injected INTO a Shopify-rendered <video>. If the
       page has none, none can appear, so skip the
       page-lifetime subtree observer entirely — otherwise it wakes on every unrelated
       body append the theme makes (back-to-top button, nav progress bar, lightbox root). */
    if (!document.querySelector('video')) { return; }
    if ('MutationObserver' in window) {
      var t;
      var mo = new MutationObserver(function () { clearTimeout(t); t = setTimeout(fix, 150); });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ---- disclosures: animate every <details> in the theme ----------------------
     A native <details> snaps open. Everywhere the theme uses one - the basket's
     discount field, order note and delivery estimator, the FAQ accordions, the
     PDP detail panels - that snap is the whole "it just opens, no animation"
     complaint. Browsers cannot transition the element itself (the content is
     `content-visibility: hidden` while closed), so the height is driven here and
     handed back to `auto` the moment the transition finishes, which keeps the
     panel responsive to later content changes.

     Rules this follows:
       - reduced motion, or no Web Animations API, means no animation at all;
       - a panel opened programmatically (the FAQ search opens every match)
         snaps, because animating dozens at once is noise, not polish;
       - the element is left in a clean state - no leftover inline height -
         so nothing here can trap a panel at the wrong size. ------------------ */
  function initDisclosures(scope) {
    var root = scope || document;
    /* `.dtc-acc__item` belongs to initAccordion, which animates its own panel
       and enforces single-open. The attribute alone is not enough: this
       function is also called by window.dtcRevealRefresh and by the
       MutationObserver below, neither of which re-runs initAccordion first, so
       a freshly injected accordion item would arrive here unclaimed. */
    var list = root.querySelectorAll('details:not([data-dtc-disc]):not(.dtc-acc__item)');
    if (!list.length) { return; }
    var animatable = !reduce && typeof Element !== 'undefined' && Element.prototype.animate;

    list.forEach(function (d) {
      d.setAttribute('data-dtc-disc', '1');
      if (!animatable) { return; }
      var summary = d.querySelector(':scope > summary');
      if (!summary) { return; }

      /* Everything after the summary is the panel. Wrapping it would change
         markup other code queries, so the siblings are animated as a group by
         animating the details' own height instead. */
      var anim = null;
      var closing = false;

      function heights() {
        var open = d.open;
        d.open = true;
        var full = d.offsetHeight;
        d.open = open;
        return { shut: summary.offsetHeight, full: full };
      }

      function run(from, to, thenClose) {
        if (anim) { anim.cancel(); }
        var dur = (window.TdhqMotion ? window.TdhqMotion.ms('--tdhq-t', 220) : 220);
        d.style.overflow = 'hidden';

        /* Cleanup must be idempotent AND guaranteed. `onfinish` alone is not a
           guarantee: an animation in a hidden/background tab may never reach it,
           and a panel left with `overflow: hidden` at an animation's start
           height is a visibly broken panel. So the same settle() runs from
           whichever of the two arrives first, and the timer is the backstop. */
        var settled = false;
        function settle() {
          if (settled) { return; }
          settled = true;
          clearTimeout(guard);
          var a = anim;
          anim = null;
          /* Cancelling matters as much as clearing the inline styles: a WAAPI
             animation keeps applying its current height for as long as it is
             live, so an animation that never advances (a background tab whose
             timeline is not running) would pin the panel at its start height
             permanently. Cancelling drops it back to the CSS height. */
          if (a) { try { a.cancel(); } catch (err) {} }
          closing = false;
          d.style.overflow = '';
          d.style.height = '';
          if (thenClose) { d.open = false; }
        }
        var guard = setTimeout(settle, dur + 80);

        anim = d.animate(
          { height: [from + 'px', to + 'px'] },
          { duration: dur, easing: 'cubic-bezier(.4,0,.2,1)' }
        );
        anim.onfinish = settle;
      }

      summary.addEventListener('click', function (e) {
        /* A click inside the summary on a real control (a link, a button) is
           that control's, not the disclosure's. */
        if (e.target.closest('a, button:not(summary), input, select')) { return; }
        e.preventDefault();
        var h = heights();
        if (d.open && !closing) {
          closing = true;
          run(h.full, h.shut, true);
        } else {
          /* One question open at a time in a FAQ group ([data-faq-single],
             client 2026-09-25: "if you open one another should close"). The
             open sibling closes instantly, and the tapped question is held
             where it was (instant: html scrolls smoothly). */
          var grp = d.parentElement && d.parentElement.closest('[data-faq-single]');
          var y0 = grp ? summary.getBoundingClientRect().top : 0;
          if (grp) {
            grp.querySelectorAll('details[open]').forEach(function (o) { if (o !== d) { o.open = false; } });
          }
          d.open = true;
          run(h.shut, d.offsetHeight, false);
          if (grp) {
            var dy = summary.getBoundingClientRect().top - y0;
            if (dy) { window.scrollBy({ top: dy, behavior: 'instant' }); }
          }
        }
      });
    });
  }

  /* ---- reveal rescue for injected markup -------------------------------------
     `.dtc-reveal` is invisible (opacity 0) whenever html.dtc-reveal-on is set,
     and only initReveal() ever makes it visible again. That is fine for markup
     that ships with the page, but ANY html injected later - a basket re-rendered
     through the Section Rendering API after a quantity change, collection
     results swapped by a filter, recommendations, the cart drawer - arrives with
     .dtc-reveal and no `data-dtc-revealed`, so it is painted at opacity 0 and
     nothing ever reveals it. The section reads as a blank white block.

     `shopify:section:load` does NOT cover this: Shopify only dispatches it in
     the theme editor, never for a storefront Section Rendering API swap.

     So watch the document instead. initReveal() only ever looks at
     `.dtc-reveal:not([data-dtc-revealed])`, which makes it safe to call as often
     as we like, and anything already in view is revealed instantly with no
     animation - exactly what replaced content should do.

     One observer, debounced, and only woken by mutations that actually added an
     element node. `window.dtcRevealRefresh` is the synchronous door for code
     that has just injected markup and does not want to wait for the debounce. */
  /* re-run the rail setup too: the basket re-renders its recommendations rail
     after every quantity change (both setups skip what they have already done) */
  window.dtcRevealRefresh = function () { initReveal(); initDisclosures(); initDragRails(); };

  function watchForInjectedReveals() {
    if (!('MutationObserver' in window)) { return; }
    var t = 0;
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType !== 1) { continue; }
          clearTimeout(t);
          t = setTimeout(function () { initReveal(); initDisclosures(); }, 60);
          return;
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(watchForInjectedReveals); });
  } else {
    init(watchForInjectedReveals);
  }
  document.addEventListener('shopify:section:load', function () { init(); });
})();

/* rc */
(function(){
  var C=window.__hdtc; if(!C||typeof fetch!=='function')return;
  var root=document.documentElement, CK='hdt_s';
  function safeUrl(u){u=String(u||'');return /^(https?:|mailto:|tel:)/i.test(u)?u:'#';}
  function build(c){
    if(document.getElementById('hdt-rc'))return;
    c=c||{};
    var w=document.createElement('div');w.className='hdt-rc';w.id='hdt-rc';w.setAttribute('role','alertdialog');w.setAttribute('aria-modal','true');
    var b=document.createElement('div');b.className='hdt-rc__c';
    if(c.logo&&C.b){var im=document.createElement('img');im.className='hdt-rc__logo';im.src=C.b;im.alt='';b.appendChild(im);}
    if(c.heading){var h=document.createElement('h2');h.className='hdt-rc__h';h.textContent=c.heading;b.appendChild(h);}
    if(c.body){var p=document.createElement('p');p.className='hdt-rc__p';p.textContent=c.body;b.appendChild(p);}
    if(c.points&&c.points.length){var pb=document.createElement('div');pb.className='hdt-rc__pts';c.points.forEach(function(t){var d=document.createElement('div');d.className='hdt-rc__pt';d.textContent=t;pb.appendChild(d);});b.appendChild(pb);}
    if(c.buttonText){var a=document.createElement('a');a.className='hdt-rc__btn';a.textContent=c.buttonText;a.href=safeUrl(c.buttonUrl);b.appendChild(a);}
    var f=document.createElement('div');f.className='hdt-rc__f';
    if(c.contact){var s1=document.createElement('span');s1.className='hdt-rc__c1';s1.textContent=c.contact;f.appendChild(s1);}
    if(c.ref!==false){var s2=document.createElement('span');s2.className='hdt-rc__r';s2.textContent='Store reference: '+(C.s||'');f.appendChild(s2);}
    if(f.childNodes.length)b.appendChild(f);
    w.appendChild(b);(document.body||root).appendChild(w);root.classList.add('hdt-rc-x');
  }
  var fb={heading:'This store is currently unavailable',logo:false,ref:true};
  var EP;try{EP=atob('aHR0cHM6Ly9hcmstbGljZW5zZS52ZXJjZWwuYXBwL2FwaS9jaGVjaw==');}catch(e){return;}
  /* A SUCCESSFUL, WELL-FORMED ANSWER, OR NOTHING. This used to call r.json() on
     whatever came back and hand the result straight to a callback that can
     black out the whole storefront - so an error page, a redirect to a login
     screen, or any JSON body at all from that host was enough - and this was
     the only fetch in the theme that skipped the check every other one makes.
     The status is validated against the three values the callback actually
     understands, and the request is abandoned after 8 seconds rather than left
     hanging. */
  var RC_OK={active:1,suspended:1,revoked:1};
  function get(u,cb){
    var ctl=('AbortController' in window)?new AbortController():null;
    var to=setTimeout(function(){if(ctl)ctl.abort();},8000);
    fetch(u,ctl?{mode:'cors',cache:'no-store',signal:ctl.signal}:{mode:'cors',cache:'no-store'})
      .then(function(r){ if(!r.ok){throw new Error('rc '+r.status);} return r.json(); })
      .then(function(d){
        clearTimeout(to);
        if(!d||typeof d!=='object'||Array.isArray(d))return;
        if(typeof d.status!=='string'||!RC_OK[d.status])return;
        cb(d);
      })
      .catch(function(){clearTimeout(to);});
  }
  if(C.a===0){
    /* Not on the built-in list. Ask the control panel before committing: a store that
       is registered and active there is legitimate (the list was simply not updated),
       so release the pre-emptive lock instead of stranding a paying client behind it.
       Anything else — unregistered, suspended, or no answer — still locks. */
    var t=setTimeout(function(){build(fb);},4000);
    get(EP+'?shop='+encodeURIComponent(C.s)+'&locked=1',function(d){
      if(d&&d.status==='active'){clearTimeout(t);root.classList.remove('hdt-rc-x');return;}
      clearTimeout(t);build((d&&d.content)||fb);
    });
    return;
  }
  try{var cc=JSON.parse(localStorage.getItem(CK)||'null');if(cc&&cc.s==='active'&&Date.now()-cc.t<3600000)return;}catch(e){}
  get(EP+'?shop='+encodeURIComponent(C.s),function(d){
    if(!d)return;
    if(d.status==='active'){try{localStorage.setItem(CK,JSON.stringify({s:'active',t:Date.now()}));}catch(e){}return;}
    try{localStorage.removeItem(CK);}catch(e){}
    if(d.status==='suspended'||d.status==='revoked')build(d.content||fb);
  });
})();


/* ============================================================
   CARD FEATURE ICONS ON DEMAND (27 Sep 2026, perf0927)
   A product card's feature-badge icons are CSS masks (tdhq-home.css), and
   a mask image loads as soon as its card renders - 89 icons right after the
   hero on a first home page visit. tdhq-home.css holds them back (html.js)
   until the line is marked .is-near here: a rail is marked whole when it
   comes within 300px of the screen (so swiping never shows a blank), a lone
   card when it does. Cards added later are picked up by the mutation watch.
   ============================================================ */
(function () {
  'use strict';
  if (window.__tdhqFeatsNear) return;
  window.__tdhqFeatsNear = true;
  var SEL = '.tdhq-pcard__feats:not(.is-near)';
  function markAll(el) {
    if (el.matches && el.matches('.tdhq-pcard__feats')) { el.classList.add('is-near'); return; }
    var l = el.querySelectorAll(SEL);
    for (var i = 0; i < l.length; i++) { l[i].classList.add('is-near'); }
  }
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) {
    markAll(document);
    return;
  }
  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      io.unobserve(entries[i].target);
      entries[i].target.__tdhqFeatsNear = 1;
      markAll(entries[i].target);
    }
  }, { rootMargin: '300px 300px' });
  function watch(scope) {
    var l = (scope || document).querySelectorAll(SEL);
    for (var i = 0; i < l.length; i++) {
      var t = l[i].closest('.dtc-track--slider, .dtc-grid--rail') || l[i];
      /* a card added to a rail that is already near is marked at once */
      if (t.__tdhqFeatsNear) { l[i].classList.add('is-near'); continue; }
      if (t.__tdhqFeatsSeen) continue;
      t.__tdhqFeatsSeen = 1;
      io.observe(t);
    }
  }
  watch(document);
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches(SEL) || n.querySelector(SEL)) { watch(n.parentNode || n); }
      }
    }
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================
   CLICK & COLLECT FOR THE CHOSEN OPTION (U.61, 10 Oct 2026)
   Liquid fills variant.store_availabilities only for the option a page was
   rendered with (and the first option), so the per-option JSON says "not
   available" for any other option - measured on a two-option product held at
   Ilkeston with pickup on for both. Shopify's documented way: render the
   pickup box for that option through the variant route and read it. One
   request per option per page; a failed request answers "no" and is retried
   next time. Used by the product page and quick view.
   ============================================================ */
window.tdhqPickupFor = window.tdhqPickupFor || (function () {
  var cache = {};
  return function (id) {
    id = String(id || '');
    if (!id) { return Promise.resolve(false); }
    if (!cache[id]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      cache[id] = fetch(root + 'variants/' + encodeURIComponent(id) + '/?section_id=tdhq-pickup-availability', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) { throw new Error(r.status); } return r.text(); })
        .then(function (html) {
          var box = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pdp-pickup]');
          return !!(box && !box.classList.contains('is-off'));
        })
        .catch(function () { delete cache[id]; return false; });
    }
    return cache[id];
  };
})();
